<template>
  <div>
    <b-card class="marks-entry-card">
      <b-row class="align-items-end filter-row">
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <b-form-group label="Class" class="mb-1">
            <v-select
              v-model="selectedClass"
              :options="classOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Select Class"
              :disabled="dataLoading || classOptions.length === 0"
              :clearable="true"
              append-to-body
              @input="onClassChange"
            />
          </b-form-group>
        </b-col>
        <b-col xl="2" lg="2" md="4" sm="6" cols="12" class="section-col">
          <b-form-group label="Section" class="mb-1">
            <v-select
              v-model="selectedSection"
              :options="sectionOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Select Section"
              :disabled="!selectedClass"
              :clearable="true"
              append-to-body
              @input="onSectionChange"
            />
          </b-form-group>
        </b-col>
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <b-form-group label="Subject" class="mb-1">
            <v-select
              v-model="selectedSubject"
              :options="subjectOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Select Subject"
              :clearable="true"
              append-to-body
              @input="onSubjectChange"
            />
          </b-form-group>
        </b-col>
        <b-col xl="2" lg="2" md="4" sm="6" cols="12" class="exam-type-col">
          <b-form-group label="Exam Type" class="mb-1">
            <v-select
              v-model="selectedExamType"
              :options="examTypes"
              label="examType"
              :reduce="(item) => item.id"
              placeholder="Select Exam Type"
              :clearable="true"
              append-to-body
            />
          </b-form-group>
        </b-col>
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <b-form-group label="Session" class="mb-1">
            <v-select
              v-model="selectedSession"
              :options="sessions"
              label="session"
              :reduce="(item) => item.id"
              placeholder="Select Session"
              :clearable="true"
              append-to-body
            />
          </b-form-group>
        </b-col>
        <b-col xl="2" lg="2" md="4" sm="6" cols="12">
          <b-form-group label="Type" class="mb-1">
            <v-select
              v-model="selectedType"
              :options="filteredTypeOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Select Type"
              :clearable="true"
              :disabled="!selectedClass || isCommentSubject"
              append-to-body
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

      <b-row v-if="dataLoading" class="text-center py-5">
        <b-col cols="12">
          <b-spinner
            style="width: 3rem; height: 3rem"
            type="grow"
            variant="primary"
            label="Spinning"
          ></b-spinner>
          <p class="mt-2">Loading classes...</p>
        </b-col>
      </b-row>

      <!-- Students List -->
      <b-row v-if="studentsLoading" class="text-center py-3">
        <b-col cols="12">
          <b-spinner
            style="width: 2rem; height: 2rem"
            type="grow"
            variant="primary"
            label="Spinning"
          ></b-spinner>
          <p class="mt-2">Loading students...</p>
        </b-col>
      </b-row>

      <b-row
        v-else-if="
          selectedClass &&
          selectedSection !== null &&
          !studentsLoading
        "
      >
        <b-col cols="12">
          <b-card class="students-list-card">
            <!-- Header with Filters (always visible when students are loaded) -->
            <div 
              v-if="selectedClass && selectedSection !== null && !studentsLoading"
              class="students-list-header d-flex flex-wrap justify-content-between align-items-center mb-1"
            >
              <div>
                <h5 class="mb-0">
                  Students · {{ selectedClassName }} · Section {{ selectedSectionName }}
                </h5>
                <small class="text-muted">Click actions to open tabulation page</small>
              </div>
              <div class="d-flex align-items-center gap-1 sort-select-wrapper">
                <!-- Search Filter -->
                <div class="search-filter-wrapper">
                  <div class="search-input-wrapper">
                    <feather-icon icon="SearchIcon" size="18" class="search-icon" />
                    <b-form-input
                      v-model="searchQuery"
                      placeholder="Search by name or GR..."
                      class="search-input"
                      debounce="300"
                    />
                    <b-button
                      v-if="searchQuery"
                      variant="link"
                      class="clear-search-btn"
                      @click="clearSearch"
                    >
                      <feather-icon icon="XIcon" size="16" />
                    </b-button>
                  </div>
                </div>
                <b-form-group
                  label=""
                  label-for="student-sort"
                  class="mb-0 mr-1 sort-selector"
                >
                  <v-select
                    id="student-sort"
                    v-model="sortBy"
                    :options="sortOptions"
                    label="label"
                    :reduce="(item) => item.value"
                    class="sort-select"
                    append-to-body
                  />
                </b-form-group>
                <b-badge variant="light-primary" pill class="students-count-badge">
                  {{ sortedStudents.length }} Students
                </b-badge>
              </div>
            </div>

            <!-- Table (only show when there are students) -->
            <template v-if="sortedStudents.length > 0">

              <b-alert
                v-if="!canOpenStudent"
                variant="warning"
                class="mb-1 selection-alert"
                show
              >
                <template v-if="isCommentSubject && (!selectedExamType || !selectedSession)">
                  Please select Exam Type and Session to give comments.
                </template>
                <template v-else>
                Please select {{ missingSelectionsLabel }} to open marks entry.
                </template>
              </b-alert>

              <b-alert
                v-if="!rights.add && !rights.edit && sortedStudents.length > 0"
                variant="danger"
                class="mb-1 selection-alert"
                show
              >
                <feather-icon icon="AlertCircleIcon" size="18" class="mr-1" />
                You do not have permission to enter or edit marks. Please contact your administrator.
              </b-alert>

              <!-- Save All Comments Button (only show when subject is Comment) -->
              <div v-if="isCommentSubject && (rights.add || rights.edit)" class="save-comments-header mb-3">
                <div class="d-flex align-items-center justify-content-between">
                  <h6 class="mb-0 text-dark font-weight-normal">
                    Student Comments
                  </h6>
                  <b-button
                    variant="gradient-success"
                    size="md"
                    class="save-all-comments-btn"
                    @click="saveAllComments"
                    :disabled="savingComments"
                  >
                    <b-spinner v-if="savingComments" small type="grow" class="mr-2" />
                    {{ savingComments ? 'Saving Comments...' : 'Save All Comments' }}
                  </b-button>
                </div>
              </div>

              <!-- Students Table -->
              <b-table
                :items="sortedStudents"
                :fields="studentFields"
                responsive
                class="students-table"
                :tbody-tr-class="rowClass"
              >
              <template #cell(sno)="row">
                <span class="text-muted font-weight-600">
                  {{ row.index + 1 }}
                </span>
              </template>

              <template #cell(student)="row">
                <div class="d-flex align-items-center position-relative">
                  <b-avatar
                    :src="row.item.picture || getDefaultAvatar(row.item.gender)"
                    size="48px"
                    :variant="row.item.marksSaved === 'partial' ? 'warning' : (row.item.marksSaved ? 'light-success' : 'light-primary')"
                    class="mr-1 student-avatar"
                  >
                    <feather-icon
                      v-if="!row.item.picture"
                      icon="UserIcon"
                      size="18"
                      :class="{
                        'student-avatar-icon-partial': row.item.marksSaved === 'partial'
                      }"
                    />
                  </b-avatar>
                  <div>
                    <div
                      class="font-weight-bold text-dark name-text"
                      :class="{
                        'text-success': row.item.marksSaved && row.item.marksSaved !== 'partial',
                        'text-warning': row.item.marksSaved === 'partial'
                      }"
                    >
                      {{ row.item.name }}
                    </div>
                    <small class="text-muted">
                      GR: {{ row.item.gr || "N/A" }}
                    </small>
                  </div>
                </div>
              </template>

              <template #cell(father)="row">
                <div class="text-dark font-weight-500">
                  {{ row.item.fatherName || "N/A" }}
                </div>
              </template>

              <template #cell(classInfo)="row">
                <div>{{ selectedClassName }}</div>
                <small class="text-muted">
                  {{ row.item.section || selectedSectionName }}
                </small>
              </template>

              <template #cell(actions)="row">
                <div class="text-right">
                  <!-- Show arrow button for Comment subject, plus button for others -->
                  <b-button
                    v-if="isCommentSubject && (rights.add || rights.edit)"
                    variant="gradient-primary"
                    size="sm"
                    class="action-arrow-btn"
                    :disabled="!selectedExamType || !selectedSession"
                    v-b-tooltip.hover.top="(!selectedExamType || !selectedSession) ? 'Please select Exam Type and Session' : (expandedComments[row.item.id] ? 'Hide comment' : 'Show comment')"
                    @click="toggleCommentArea(row.item.id)"
                  >
                    <feather-icon 
                      :icon="expandedComments[row.item.id] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                      size="16" 
                    />
                  </b-button>
                  <b-button
                    v-else-if="rights.add || rights.edit"
                    variant="gradient-primary"
                    size="sm"
                    class="action-plus-btn"
                    v-b-tooltip.hover.top="'Open marks entry'"
                    @click="viewStudentDetails(row.item)"
                    :disabled="!canOpenStudent || loadingStudentId === row.item.id"
                  >
                    <b-spinner 
                      v-if="loadingStudentId === row.item.id" 
                      small 
                      type="grow"
                    />
                    <feather-icon 
                      v-else
                      icon="PlusIcon" 
                      size="16" 
                    />
                  </b-button>
                </div>
              </template>

              <!-- Row Details Slot for Comments (only for Comment subject) -->
              <template #row-details="row">
                <div v-if="isCommentSubject" class="comment-row-details">
                  <div class="comment-card-modern">
                    <div class="comment-header-modern">
                      <div class="comment-title-wrapper">
                        <feather-icon icon="MessageSquareIcon" size="20" class="comment-title-icon" />
                        <label class="comment-label-modern mb-0">
                          Student Comment
                        </label>
                      </div>
                      <div class="comment-actions-wrapper">
                        <b-button
                          variant="gradient-info"
                          size="sm"
                          class="preview-marks-btn-modern"
                          @click="openPreviewMarksModal(row.item.id)"
                        >
                          <feather-icon icon="EyeIcon" size="14" class="mr-1" />
                          Preview Marks
                        </b-button>
                        <b-button
                          v-if="rights.add || rights.edit"
                          variant="gradient-primary"
                          size="sm"
                          class="select-comment-btn-modern"
                          @click="openCommentModal(row.item.id)"
                        >
                          <feather-icon icon="SearchIcon" size="14" class="mr-1" />
                          Select Comment
                        </b-button>
                      </div>
                      </div>
                      <b-form-textarea
                        v-model="studentComments[row.item.id]"
                        placeholder="Write a comment for this student or select from comment bank..."
                      rows="4"
                      class="comment-textarea-modern"
                        :disabled="!rights.add && !rights.edit"
                      />
                  </div>
                </div>
              </template>
              </b-table>
            </template>

            <!-- Empty State (only show when there are no students) -->
            <template v-else>
              <b-alert
                v-if="!canOpenStudent"
                variant="warning"
                class="mb-1 selection-alert"
                show
              >
                <template v-if="isCommentSubject && (!selectedExamType || !selectedSession)">
                  Please select Exam Type and Session to give comments.
                </template>
                <template v-else>
                Please select {{ missingSelectionsLabel }} to open marks entry.
                </template>
              </b-alert>

              <div class="empty-state-box">
                <div class="empty-state-content">
                  <div class="empty-state-icon">
                    <feather-icon icon="UsersIcon" size="64" />
                  </div>
                  <h4 class="empty-state-title">No Students Found</h4>
                  <p class="empty-state-message" v-if="searchQuery">
                    No students match your search query "{{ searchQuery }}".
                  </p>
                  <p class="empty-state-message" v-else>
                    There is no data saved for the selected filters.
                  </p>
                  <p class="empty-state-submessage" v-if="searchQuery">
                    Try clearing your search or adjusting the search terms.
                  </p>
                  <p class="empty-state-submessage" v-else>
                    Please try selecting a different class, section, or check your filters.
                  </p>
                </div>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <!-- Comment Selection Modal -->
      <b-modal
        id="comment-selection-modal"
        ref="commentSelectionModal"
        title="Select Comment"
        size="lg"
        centered
        :hide-footer="true"
        class="comment-selection-modal"
        @hidden="closeCommentModal"
      >
        <template #modal-header="{ close }">
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center">
              <h5 class="mb-0">Select Comment from Bank</h5>
            </div>
            <b-button-close @click="close()" class="ml-auto">
              <feather-icon icon="XIcon" size="18" />
            </b-button-close>
          </div>
        </template>

        <div class="comment-modal-content">
          <!-- Search Bar -->
          <div class="comment-search-wrapper">
            <div class="search-input-container">
              <feather-icon icon="SearchIcon" size="18" class="search-icon" />
              <b-form-input
                v-model="commentSearchQuery"
                placeholder="Search comments..."
                class="comment-search-input"
                @input="onCommentSearchChange"
                debounce="300"
              />
              <b-button
                v-if="commentSearchQuery"
                variant="link"
                class="clear-search-btn"
                @click="clearCommentSearch"
              >
                <feather-icon icon="XIcon" size="16" />
              </b-button>
            </div>
            <div v-if="commentTotalCount > 0" class="search-results-count">
              {{ commentTotalCount }} comment{{ commentTotalCount !== 1 ? 's' : '' }} found
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loadingComments" class="text-center py-5">
            <b-spinner type="grow" variant="primary" style="width: 3rem; height: 3rem;" />
            <p class="mt-3 text-muted font-weight-600">Loading comments...</p>
          </div>

          <!-- Comments List -->
          <div v-else-if="filteredComments.length > 0" class="comments-list-container comments-list-scrollable">
            <div
              v-for="comment in filteredComments"
              :key="comment.id"
              class="comment-item-card"
              @click="selectComment(comment)"
            >
              <div class="comment-item-content">
                <div class="comment-text-preview">
                  <feather-icon icon="MessageSquareIcon" size="16" class="comment-icon" />
                  <span>{{ comment.remarks || comment.comment || 'No comment text' }}</span>
                </div>
                <div v-if="comment.category || comment.createdDate" class="comment-meta">
                  <span v-if="comment.category" class="comment-category">
                    <feather-icon icon="TagIcon" size="12" class="mr-1" />
                    {{ comment.category }}
                  </span>
                  <span v-if="comment.createdDate" class="comment-date">
                    <feather-icon icon="CalendarIcon" size="12" class="mr-1" />
                    {{ formatDate(comment.createdDate) }}
                  </span>
                </div>
              </div>
              <div class="comment-select-arrow">
                <feather-icon icon="ChevronRightIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loadingComments" class="empty-comments-state">
            <div class="empty-state-icon-wrapper">
              <feather-icon icon="FileTextIcon" size="64" class="empty-icon" />
            </div>
            <h6 class="empty-state-title">
              {{ commentSearchQuery ? 'No comments found' : 'No comments available' }}
            </h6>
            <p class="empty-state-message">
              {{ commentSearchQuery ? 'Try adjusting your search terms' : 'Comments will appear here once added to the comment bank' }}
            </p>
          </div>

          <!-- Pagination -->
          <div v-if="commentTotalCount > commentPerPage && !loadingComments" class="comment-pagination-wrapper">
            <div class="pagination-info">
              Showing {{ ((commentCurrentPage - 1) * commentPerPage) + 1 }} to 
              {{ Math.min(commentCurrentPage * commentPerPage, commentTotalCount) }} of 
              {{ commentTotalCount }} comments
            </div>
            <b-pagination
              v-model="commentCurrentPage"
              :total-rows="commentTotalCount"
              :per-page="commentPerPage"
              size="sm"
              align="center"
              @change="onCommentPageChange"
              class="comment-pagination"
            />
          </div>
        </div>
      </b-modal>

      <!-- Preview Marks Modal -->
      <b-modal
        id="preview-marks-modal"
        ref="previewMarksModal"
        title="Student Marks Preview"
        size="xl"
        centered
        :hide-footer="true"
        class="preview-marks-modal"
        @hidden="closePreviewMarksModal"
      >
        <template #modal-header="{ close }">
          <div class="preview-marks-modal-header">
            <div class="header-content">
              <div class="header-icon-wrapper">
                <feather-icon icon="FileTextIcon" size="24" />
              </div>
              <div class="header-text">
                <h5 class="mb-0">Student Marks Preview</h5>
                <small class="text-muted">View detailed marks information</small>
              </div>
            </div>
            <b-button-close @click="close()" class="modal-close-btn">
              <feather-icon icon="XIcon" size="18" />
            </b-button-close>
          </div>
        </template>

        <div class="preview-marks-content">
          <!-- Loading State -->
          <div v-if="loadingMarks" class="text-center py-5">
            <b-spinner type="grow" variant="primary" style="width: 3rem; height: 3rem;" />
            <p class="mt-3 text-muted font-weight-600">Loading marks...</p>
          </div>

          <!-- Marks and/or comments (API may return comments only with empty data[]) -->
          <div v-else-if="previewMarksModalHasContent" class="marks-preview-container">
          

            <!-- Student Info Header (from marks row, roster, or comment metadata) -->
            <div class="student-info-header">
              <div class="student-info-card">
                <div class="info-grid">
                  <div class="info-item" :style="{ animationDelay: '0.05s' }">
                    <div class="info-icon-wrapper">
                      <feather-icon icon="UserIcon" size="18" />
                    </div>
                    <div class="info-content">
                      <span class="info-label">Student Name</span>
                      <span class="info-value">{{ previewStudentDisplay.studentName }}</span>
                    </div>
                  </div>
                  <div class="info-item" :style="{ animationDelay: '0.1s' }">
                    <div class="info-icon-wrapper">
                      <feather-icon icon="HashIcon" size="18" />
                    </div>
                    <div class="info-content">
                      <span class="info-label">GR Number</span>
                      <span class="info-value">{{ previewStudentDisplay.studentGR }}</span>
                    </div>
                  </div>
                  <div class="info-item" :style="{ animationDelay: '0.15s' }">
                    <div class="info-icon-wrapper">
                      <feather-icon icon="BookOpenIcon" size="18" />
                    </div>
                    <div class="info-content">
                      <span class="info-label">Class</span>
                      <span class="info-value">{{ previewStudentDisplay.classSection }}</span>
                    </div>
                  </div>
                  <div class="info-item" :style="{ animationDelay: '0.2s' }">
                    <div class="info-icon-wrapper">
                      <feather-icon icon="CalendarIcon" size="18" />
                    </div>
                    <div class="info-content">
                      <span class="info-label">Session</span>
                      <span class="info-value">{{ previewStudentDisplay.sessionName }}</span>
                    </div>
                  </div>
                  <div class="info-item" :style="{ animationDelay: '0.25s' }">
                    <div class="info-icon-wrapper">
                      <feather-icon icon="ClipboardIcon" size="18" />
                    </div>
                    <div class="info-content">
                      <span class="info-label">Exam Type</span>
                      <span class="info-value">{{ previewStudentDisplay.examTypeName }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Marks Table (excludes Comment / Promotion Tag; FTA when Final Term Aggregate) -->
            <div
              v-if="previewActiveMarksRows.length > 0"
              class="preview-marks-block"
              :class="{ 'preview-marks-block--assessment': isFinalTermAggregateSelected }"
            >
              <div
                v-if="isFinalTermAggregateSelected"
                class="preview-section-header"
              >
                <div class="preview-section-header-icon">
                  <feather-icon icon="ClipboardIcon" size="20" />
                </div>
                <div class="preview-section-header-text">
                  <h6 class="preview-section-title mb-0">
                    {{ finalTermAssessmentExamTypeName }}
                  </h6>
                  <small class="preview-section-subtitle">
                    Subject-wise marks breakdown
                  </small>
                </div>
              </div>

              <div class="marks-table-wrapper">
                <b-table
                  :items="previewActiveMarksRows"
                  :fields="marksPreviewFields"
                  striped
                  hover
                  class="marks-preview-table"
                >
                <template #cell(index)="row">
                  <span class="text-muted font-weight-600">
                    {{ row.index + 1 }}
                  </span>
                </template>

                <template #cell(subjectName)="row">
                  <div class="subject-name-cell">
                    <feather-icon icon="BookIcon" size="16" class="subject-icon" />
                    <span class="font-weight-bold">{{ row.item.subjectName }}</span>
                  </div>
                </template>

                <template #cell(teacherName)="row">
                  <div class="teacher-name-cell">
                    <span>{{ row.item.teacherName || 'N/A' }}</span>
                  </div>
                </template>

                <template #cell(marks)="row">
                  <div class="marks-cell">
                    <span class="obtained-marks" :class="getMarksColorClass(row.item)">
                      {{ row.item.obtainedMarks }}
                    </span>
                    <span class="marks-separator">/</span>
                    <span class="total-marks">{{ row.item.totalMarks }}</span>
                  </div>
                </template>

                <template #cell(percentage)="row">
                  <b-badge
                    :variant="getPercentageVariant(row.item.meta_subj_percenatge)"
                    class="percentage-badge"
                  >
                    {{ row.item.meta_subj_percenatge ? row.item.meta_subj_percenatge.toFixed(2) + '%' : 'N/A' }}
                  </b-badge>
                </template>

                <template #cell(grade)="row">
                  <b-badge
                    :variant="getGradeVariant(row.item.meta_sub_grade)"
                    class="grade-badge"
                  >
                    {{ row.item.meta_sub_grade || '-' }}
                  </b-badge>
                </template>

                <template #cell(status)="row">
                  <b-badge
                    :variant="row.item.status === 'PASSED' ? 'success' : row.item.status === 'FAIL' ? 'danger' : 'secondary'"
                    class="status-badge"
                  >
                    {{ row.item.status || '-' }}
                  </b-badge>
                </template>
              </b-table>
              </div>
            </div>

          

            <!-- Comments by exam type (shown when API returns commentsByExamType; may be without marks data[]) -->
            <div
              v-if="previewCommentsByExamType.length > 0"
              class="preview-remarks-by-exam"
            >
              <h6 class="summary-title mb-2">Comments</h6>

              <div
                v-for="examBlock in previewCommentsByExamType"
                :key="examBlock.examTypeKey"
                class="exam-type-remarks-block"
              >
                <div class="exam-type-remarks-heading">
                  {{ examBlock.examTypeName }}
                </div>
                <ul class="exam-type-remarks-list list-unstyled mb-0">
                  <li
                    v-for="(c, cIdx) in examBlock.comments"
                    :key="`${c.marksID}-${c.subjectID}-${cIdx}`"
                    class="exam-type-remark-item"
                  >
                    <span class="remark-body">{{
                      formatPreviewRemarkBody(c.comment || c.remarks)
                    }}</span>
                  </li>
                </ul>
              </div>
            </div>

              <!-- Summary Statistics (only real subjects; excludes "Comment") -->
              <div v-if="previewActiveMarksRows.length > 0" class="marks-summary" style="margin-top: 1.5rem;">
              <h6 class="summary-title">Performance Summary</h6>
              <div class="summary-cards">
                <div class="summary-card">
                  <feather-icon icon="TrendingUpIcon" size="24" class="summary-icon text-success" />
                  <div class="summary-content">
                    <span class="summary-label">Total Subjects</span>
                    <span class="summary-value">{{ previewActiveMarksRows.length }}</span>
                  </div>
                </div>
                <div class="summary-card">
                  <feather-icon icon="CheckCircleIcon" size="24" class="summary-icon text-success" />
                  <div class="summary-content">
                    <span class="summary-label">Passed</span>
                    <span class="summary-value">{{ getPassedCount }}</span>
                  </div>
                </div>
                <div class="summary-card">
                  <feather-icon icon="XCircleIcon" size="24" class="summary-icon text-danger" />
                  <div class="summary-content">
                    <span class="summary-label">Failed</span>
                    <span class="summary-value">{{ getFailedCount }}</span>
                  </div>
                </div>
                <div class="summary-card">
                  <feather-icon icon="BarChartIcon" size="24" class="summary-icon text-primary" />
                  <div class="summary-content">
                    <span class="summary-label">Average</span>
                    <span class="summary-value">{{ getAveragePercentage }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loadingMarks" class="empty-marks-state">
            <div class="empty-state-icon-wrapper">
              <feather-icon icon="FileTextIcon" size="64" class="empty-icon" />
            </div>
            <h6 class="empty-state-title">No marks found</h6>
            <p class="empty-state-message">
              No marks data available for this student
            </p>
          </div>
        </div>
      </b-modal>

  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BFormGroup,
  BFormSelect,
  BFormTextarea,
  BFormInput,
  BAvatar,
  BAlert,
  BModal,
  BButtonClose,
  BPagination,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import vSelect from "vue-select";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BFormGroup,
    BFormTextarea,
    BFormInput,
    BAvatar,
    BAlert,
    BModal,
    BButtonClose,
    BPagination,
    vSelect,
  },
  data() {
    return {
      rights: {},
      dataLoading: false,
      studentsLoading: false,
      loadingStudentId: null, // Track which student's plus button is loading
      classesData: [],
      studentsData: [],
      staffID: null, // Store the teacher ID (tID) from GetByUserLinkID API
      selectedClass: null,
      selectedSection: null,
      selectedSubject: null,
      selectedClassName: "",
      selectedSectionName: "",
      classOptions: [],
      sectionOptions: [],
      subjectOptions: [],
      examTypes: [],
      sessions: [],
      selectedExamType: null,
      selectedSession: null,
      selectedType: null,
      isRestoringFilters: false,
      typeOptions: [
        { label: "Icon Base", value: "icon-base" },
        { label: "Questions Base", value: "questions-base" },
        { label: "Class Test", value: "class-test" },
      ],
      sortBy: "name",
      sortOptions: [
        { value: "name", label: "Name (A → Z)" },
        { value: "gr", label: "GR Number" },
      ],
      searchQuery: "", // Search filter for name and GR
      studentFields: [
        { key: "sno", label: "S.No", class: "text-center sno-col" },
        { key: "student", label: "Student" },
        { key: "father", label: "Father Name" },
        { key: "classInfo", label: "Class / Section" },
        { key: "actions", label: "Actions", class: "text-right" },
      ],
      // Comment-related data
      studentComments: {}, // Object to store comments: { studentId: "comment text" }
      studentCommentIds: {}, // Object to store comment IDs for updates: { studentId: { optionID, categoryID } }
      expandedComments: {}, // Object to track which comment areas are expanded: { studentId: true/false }
      savingComments: false,
      existingComments: [], // Store loaded comments from API
      // Comment modal data
      commentModalStudentId: null,
      commentBankComments: [], // Comments from comment bank API
      filteredComments: [], // Filtered comments based on search
      commentSearchQuery: "",
      loadingComments: false,
      commentCurrentPage: 1,
      commentPerPage: 20,
      commentTotalCount: 0,
      commentSearchTimeout: null,
      // Preview marks data
      previewMarksModalStudentId: null,
      previewMarksData: [],
      /** Marks for Final Term Assessment when previewing Final Term Aggregate */
      previewFinalTermAssessmentMarks: [],
      /** Tabulation API commentsByExamType, filtered to preview student only */
      previewCommentsByExamType: [],
      loadingMarks: false,
      marksPreviewFields: [
        { key: 'index', label: '#', class: 'text-center' },
        { key: 'subjectName', label: 'Subject', sortable: true },
        { key: 'teacherName', label: 'Teacher', sortable: true },
        { key: 'marks', label: 'Marks', class: 'text-center' },
        { key: 'percentage', label: 'Percentage', class: 'text-center' },
        { key: 'grade', label: 'Grade', class: 'text-center' },
        { key: 'status', label: 'Status', class: 'text-center' },
      ],
    };
  },
  computed: {
    filteredStudents() {
      // Filter students by search query (name or GR)
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        return [...this.studentsData];
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.studentsData.filter((student) => {
        const name = (student.name || "").toLowerCase();
        const gr = (student.gr || "").toLowerCase();
        return name.includes(query) || gr.includes(query);
      });
    },
    sortedStudents() {
      const list = [...this.filteredStudents];
      if (this.sortBy === "gr") {
        return list.sort((a, b) =>
          (a.gr || "").localeCompare(b.gr || "", undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      }
      return list.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
    },
    filteredTypeOptions() {
      return this.typeOptions;
    },
    missingSelections() {
      const missing = [];
      if (!this.selectedClass) missing.push("class");
      if (!this.selectedSubject) missing.push("subject");
      // Exam Type and Session are required only for Comment subject
      if (this.isCommentSubject) {
      if (!this.selectedExamType) missing.push("exam type");
      if (!this.selectedSession) missing.push("session");
      }
      // Type is not required if subject is Comment
      if (!this.isCommentSubject && !this.selectedType) missing.push("type");
      return missing;
    },
    canOpenStudent() {
      return this.missingSelections.length === 0;
    },
    missingSelectionsLabel() {
      if (this.canOpenStudent) return "";
      return this.missingSelections
        .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
        .join(", ");
    },
    isCommentSubject() {
      if (!this.selectedSubject) return false;
      const subject = this.subjectOptions.find(s => s.value === this.selectedSubject);
      if (!subject) return false;
      return subject.label && subject.label.toUpperCase().trim() === 'COMMENT';
    },
    /** Marks preview table: exclude pseudo-subjects (Comment, Promotion Tag) */
    previewMarksTableRows() {
      return this.filterPreviewMarksRows(this.previewMarksData);
    },
    previewFinalTermAssessmentTableRows() {
      return this.filterPreviewMarksRows(this.previewFinalTermAssessmentMarks);
    },
    /** Marks table + performance summary: FTA rows when previewing Final Term Aggregate */
    previewActiveMarksRows() {
      if (
        this.isFinalTermAggregateSelected &&
        this.previewFinalTermAssessmentTableRows.length > 0
      ) {
        return this.previewFinalTermAssessmentTableRows;
      }
      return this.previewMarksTableRows;
    },
    isFinalTermAggregateSelected() {
      return (
        this.getExamTypeNameById(this.selectedExamType)
          .trim()
          .toUpperCase() === "FINAL TERM AGGREGATE"
      );
    },
    finalTermAssessmentExamTypeId() {
      return this.findExamTypeIdByName("Final Term Assessment");
    },
    finalTermAssessmentExamTypeName() {
      const id = this.finalTermAssessmentExamTypeId;
      return id ? this.getExamTypeNameById(id) : "Final Term Assessment";
    },
    /** Preview modal: show panel when visible marks rows and/or comments-by-exam exist */
    previewMarksModalHasContent() {
      const hasMarks = this.previewMarksTableRows.length > 0;
      const hasFinalTermAssessmentMarks =
        this.previewFinalTermAssessmentTableRows.length > 0;
      const hasComments =
        Array.isArray(this.previewCommentsByExamType) &&
        this.previewCommentsByExamType.length > 0;
      return hasMarks || hasFinalTermAssessmentMarks || hasComments;
    },
    /** Student header in preview: prefer first marks row, else roster + comment API fields */
    previewStudentDisplay() {
      const row = this.previewMarksTableRows[0] || this.previewMarksData[0];
      if (row) {
        const cls = row.className || "";
        const sec = row.sectionName || "";
        const classSection =
          cls || sec ? `${cls}${cls && sec ? " - " : ""}${sec}` : "—";
        return {
          studentName: row.studentName || "—",
          studentGR: row.studentGR || "—",
          classSection,
          sessionName: row.sessionName || "—",
          examTypeName: row.examTypeName || "—",
        };
      }
      const rosterId = this.previewMarksModalStudentId;
      const roster =
        rosterId != null
          ? this.studentsData.find((s) => Number(s.id) === Number(rosterId))
          : null;
      let examTypeName = "—";
      let commentRow = null;
      for (const block of this.previewCommentsByExamType) {
        const comments = block.comments || [];
        const match =
          rosterId != null
            ? comments.find((c) => Number(c.sID) === Number(rosterId))
            : null;
        const c = match || comments[0];
        if (c) {
          commentRow = c;
          examTypeName = block.examTypeName || examTypeName;
          break;
        }
      }
      const cls = roster?.class || this.selectedClassName || "";
      const sec = roster?.section || this.selectedSectionName || "";
      const classSection =
        cls || sec ? `${cls}${cls && sec ? " - " : ""}${sec}` : "—";
      const sessionName =
        this.selectedSession != null
          ? this.sessions.find((s) => s.id === this.selectedSession)?.session ||
            "—"
          : "—";
      return {
        studentName: commentRow?.studentName || roster?.name || "—",
        studentGR: roster?.gr || "—",
        classSection: classSection !== "" ? classSection : "—",
        sessionName,
        examTypeName,
      };
    },
    getPassedCount() {
      return this.previewActiveMarksRows.filter(item => item.status === 'PASSED').length;
    },
    getFailedCount() {
      return this.previewActiveMarksRows.filter(item => item.status === 'FAIL').length;
    },
    getAveragePercentage() {
      if (this.previewActiveMarksRows.length === 0) return '0.00';
      const validPercentages = this.previewActiveMarksRows.filter(item => item.meta_subj_percenatge !== null);
      if (validPercentages.length === 0) return '0.00';
      const sum = validPercentages.reduce((acc, item) => acc + (item.meta_subj_percenatge || 0), 0);
      return (sum / validPercentages.length).toFixed(2);
    },
  },
  async created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    console.log('Marks Entry Simple - Route name:', this.$route.name);
    console.log('Available rights:', this.$store.state.rights);
    
    if (!this.$store.state.rights[this.$route.name]) {
      console.log('No rights found for route:', this.$route.name);
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    console.log('User rights for marks entry:', this.rights);
    
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await Promise.all([
      this.loadTeacherClasses(),
      this.loadExamTypes(),
      this.loadSessions(),
    ]);
    // Restore filters after data is loaded
    this.restoreFilters();
  },
  watch: {
    selectedClass(newVal, oldVal) {
      if (!this.isRestoringFilters) {
        this.saveFilters();
        
        // Clear type when class is removed
        if (!newVal && oldVal) {
          this.selectedType = null;
        }
      }
    },
    selectedSection() {
      if (!this.isRestoringFilters) {
        this.saveFilters();
      }
    },
    async selectedSubject(newVal, oldVal) {
      if (!this.isRestoringFilters) {
        this.saveFilters();
        
        // Clear type if subject is Comment (Comment doesn't need type)
        if (this.isCommentSubject) {
          this.selectedType = null;
        }
        
        // Reload students when subject changes (to update marksSaved status)
        // This includes when subject is cleared (set to null)
        if (this.selectedClass && this.selectedSection !== null) {
          await this.loadStudents();
          // Comments are now extracted directly from commentData in loadStudents()
          // No need to call loadComments() separately
          if (!this.isCommentSubject) {
            // Clear comments if subject is not Comment
            this.studentComments = {};
            this.studentCommentIds = {};
            this.expandedComments = {};
            this.existingComments = [];
          }
        } else {
          // Clear comments if filters are not complete
          this.studentComments = {};
          this.studentCommentIds = {};
          this.expandedComments = {};
          this.existingComments = [];
        }
      }
    },
    selectedExamType(newVal, oldVal) {
      if (!this.isRestoringFilters) {
        this.saveFilters();
        // Reload students when exam type changes (to update marksSaved status)
        // This includes when exam type is cleared (set to null)
        if (this.selectedClass && this.selectedSection !== null) {
          this.loadStudents();
        }
      }
    },
    selectedSession(newVal, oldVal) {
      if (!this.isRestoringFilters) {
        this.saveFilters();
        // Reload students when session changes (to update marksSaved status)
        // This includes when session is cleared (set to null)
        if (this.selectedClass && this.selectedSection !== null) {
          this.loadStudents();
        }
      }
    },
    selectedType(newVal, oldVal) {
      if (!this.isRestoringFilters) {
        this.saveFilters();
        // Reload students when type changes (to update marksSaved status)
        // This includes when type is cleared (set to null)
        if (this.selectedClass && this.selectedSection !== null) {
          this.loadStudents();
        }
      }
    },
  },
  methods: {
    ...mapActions({
      get: "get",
      post: "post",
      put: "put",
      delete: "delete",
    }),
    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      if (item.marksSaved === 'partial') {
        return 'marks-partial-row'
      }
      if (item.marksSaved) {
        return 'marks-saved-row'
      }
      return ''
    },
    clearSearch() {
      this.searchQuery = "";
    },
    async loadTeacherClasses() {
      this.dataLoading = true;
      try {
        const userLinkID =
          this.$store.state.userData.userID || this.$store.state.userData.localID;
        const cID = this.$store.state.userData.cId;
        const dbName = encodeURIComponent(this.$store.state.userData.db);

        if (!userLinkID || !cID) {
          this.$bvToast.toast("User ID or Campus ID is missing", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
          this.dataLoading = false;
          return;
        }

        // First, get staff ID from GetByUserLinkID API
        const staffUrl = `${this.$store.state.domain}Staff/GetByUserLinkID?db=${dbName}&userLinkID=${userLinkID}&cID=${cID}`;

        const staffResponse = await this.get({
          url: staffUrl,
          token: this.$store.state.userData.token,
        });

        // Extract staff ID from response
        let staffID = null;
        if (staffResponse && staffResponse.id) {
          staffID = staffResponse.id;
        } else if (staffResponse && staffResponse.data && staffResponse.data.id) {
          staffID = staffResponse.data.id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse) &&
          staffResponse.length > 0 &&
          staffResponse[0].id
        ) {
          staffID = staffResponse[0].id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse.data) &&
          staffResponse.data.length > 0 &&
          staffResponse.data[0].id
        ) {
          staffID = staffResponse.data[0].id;
        }

        if (!staffID) {
          this.$bvToast.toast("Failed to get staff ID", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
          this.dataLoading = false;
          return;
        }

        // Store the staff ID for later use
        this.staffID = staffID;

        // Now use the staff ID as tID in LoadData API
        const url = `${this.$store.state.domain}teacherClasses/LoadData?db=${dbName}&tID=${staffID}&cID=${cID}`;

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        // Handle different response structures
        let data = [];
        if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response && response.status === "success" && response.data) {
          data = Array.isArray(response.data) ? response.data : [];
        }

        this.classesData = data;
        this.prepareClassOptions();
      } catch (error) {
        console.error("Error loading teacher classes:", error);
        this.$bvToast.toast("Failed to load classes", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.classesData = [];
      } finally {
        this.dataLoading = false;
      }
    },
    prepareClassOptions() {
      const classMap = new Map();
      this.classesData.forEach((classItem) => {
        if (!classMap.has(classItem.cID)) {
          classMap.set(classItem.cID, {
            value: classItem.cID,
            label: classItem.className,
            data: classItem,
          });
        }
      });
      this.classOptions = Array.from(classMap.values());
    },
    getSectionOptions(secSubjectsData = []) {
      const options = [];
      if (Array.isArray(secSubjectsData)) {
        secSubjectsData.forEach((sec) => {
          options.push({
            value: sec.sectionID,
            label: sec.sectionName,
            data: sec,
          });
        });
      }
      return options;
    },
    onClassChange(classID) {
      this.selectedClass = classID;
      this.selectedSection = null;
      this.selectedSubject = null;
      this.sectionOptions = [];
      this.subjectOptions = [];
      this.studentsData = [];
      
      // Always clear type when class changes to show correct options for new class
      this.selectedType = null;

      if (classID) {
        const classItem = this.classesData.find((c) => c.cID === classID);
        this.sectionOptions = this.getSectionOptions(
          classItem?.secSubjectsData || []
        );
        this.selectedClassName = classItem?.className || "";
        this.selectedSection = null;
        this.selectedSectionName = "";
        this.prepareSubjectOptions(classItem);
        // Don't load students until section is selected
      } else {
        this.selectedClassName = "";
        this.subjectOptions = [];
        this.selectedSubject = null;
      }
    },
    onSectionChange(sectionID) {
      this.selectedSection = sectionID;
      this.selectedSubject = null;
      this.subjectOptions = [];
      this.studentsData = [];

      if (this.selectedClass == null || sectionID === null) return;

      const classItem = this.classesData.find((c) => c.cID === this.selectedClass);
      if (classItem && Array.isArray(classItem.secSubjectsData)) {
        const section = classItem.secSubjectsData.find(
          (s) => s.sectionID === sectionID
        );
        if (section) {
          this.selectedSectionName = section.sectionName;
          this.subjectOptions = (section.subjects || []).map((sub) => ({
            value: sub.id,
            label: sub.subject,
            data: sub,
          }));
        }
      }
      this.loadStudents();
    },
    prepareSubjectOptions(classItem) {
      if (!classItem) {
        this.subjectOptions = [];
        this.selectedSubject = null;
        return;
      }
      const subjects = new Map();
      (classItem.secSubjectsData || []).forEach((section) => {
        (section.subjects || []).forEach((sub) => {
          if (!subjects.has(sub.id)) {
            subjects.set(sub.id, {
              value: sub.id,
              label: sub.subject,
              data: sub,
            });
          }
        });
      });
      this.subjectOptions = Array.from(subjects.values());
      if (
        this.selectedSubject &&
        !this.subjectOptions.some((opt) => opt.value === this.selectedSubject)
      ) {
        this.selectedSubject = null;
      }
    },
    onSubjectChange(subjectID) {
      this.selectedSubject = subjectID;
    },
    async loadStudents() {
      if (!this.selectedClass || this.selectedSection === null || this.selectedSection === undefined) return;

      this.studentsLoading = true;
      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const clsID = this.selectedClass;
        const sectionID = this.selectedSection || 0;

        // Build URL with optional parameters
        let url = `${this.$store.state.domain}students/FilterDataWithMarks?cls=${clsID}&sec=${sectionID}&status=present&db=${dbName}&cID=${cID}&page=1`;
        
        // Add optional parameters only if they are selected
        // These should always be sent so backend can correctly
        // determine if marks already exist (marksSaved flag).
        if (this.selectedSubject) {
          url += `&subjectID=${this.selectedSubject}`;
        }
        if (this.selectedSession) {
          url += `&sessionID=${this.selectedSession}`;
        }
        if (this.selectedExamType) {
          url += `&examTypeID=${this.selectedExamType}`;
        }

        // Pass type to match tabulation sheet configuration:
        // - For Comment subject we send type=comment
        // - For regular subjects we send the selected type
        if (this.isCommentSubject) {
          url += `&type=comment`;
        } else if (this.selectedType) {
          url += `&type=${encodeURIComponent(this.selectedType)}`;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        let students = [];
        if (response) {
          // Handle nested data structure: response.data.data (from the API response structure shown)
          if (response.status === "success" && response.data && response.data.data && Array.isArray(response.data.data)) {
            students = response.data.data;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            students = response.data.data;
          } else if (Array.isArray(response.data)) {
            students = response.data;
          } else if (Array.isArray(response)) {
            students = response;
          } else if (
            response.status === "success" &&
            Array.isArray(response.data)
          ) {
            students = response.data;
          }
        }
        
        // Debug: Log students data to check commentData
        console.log('API Response structure:', {
          hasResponse: !!response,
          responseStatus: response?.status,
          hasData: !!response?.data,
          hasNestedData: !!response?.data?.data,
          studentsCount: students.length,
          firstStudentCommentData: students[0]?.commentData
        });
        console.log('Loaded students with commentData:', students.map(s => ({
          id: s.id,
          name: s.name,
          hasCommentData: !!s.commentData,
          commentData: s.commentData
        })));

        this.studentsData = students.map((student) => {
          // Normalize marksSaved so we can distinguish between
          // fully saved (true) and partially saved ('partial')
          let marksSaved = student.marksSaved;
          if (typeof marksSaved === 'string') {
            const val = marksSaved.toLowerCase().trim();
            if (val === 'partial') {
              marksSaved = 'partial';
            } else if (val === 'true' || val === '1' || val === 'yes' || val === 'saved') {
              marksSaved = true;
            } else {
              marksSaved = false;
            }
          } else if (typeof marksSaved === 'number') {
            // Treat any positive number as "saved"
            marksSaved = marksSaved > 0;
          } else {
            marksSaved = !!marksSaved;
          }

          const studentObj = {
            id: student.id,
            name: student.name || student.studentName || "N/A",
            gr:
              student.gr ||
              student.grNo ||
              student.registrationNumber ||
              student.grNumber ||
              "N/A",
            fatherName:
              student.fatherName ||
              student.father_name ||
              student.father ||
              student.fatherName ||
              "N/A",
            picture: student.picture || student.photo || student.image || null,
            gender: student.gender || "male",
            class: this.selectedClassName,
            section: student.section || student.sectionName || this.selectedSectionName,
            isMarked: false,
            // normalized marksSaved value
            marksSaved,
            _showDetails: false, // Initialize row details as collapsed
          };
          return studentObj;
        });

        // Extract comments from commentData if subject is Comment
        if (this.isCommentSubject) {
          // Create new objects to ensure reactivity
          const newComments = {};
          const newCommentIds = {};
          
          // Wait for studentsData to be reactive, then extract comments
          await this.$nextTick();
          
          // Build comments object from API response
          students.forEach((student) => {
            // Debug: Log each student's commentData
            console.log(`Student ${student.id} (${student.name}):`, {
              hasCommentData: !!student.commentData,
              commentData: student.commentData,
              marksData: student.commentData?.marksData
            });
            
            // Initialize with empty values
            newComments[student.id] = '';
            newCommentIds[student.id] = {
              optionID: null,
              categoryID: null,
              detailId: null, // Store detail record ID for updates
            };
            
            if (student.commentData && student.commentData.marksData && Array.isArray(student.commentData.marksData) && student.commentData.marksData.length > 0) {
              // Get the first comment (assuming one comment per student)
              const commentItem = student.commentData.marksData[0];
              console.log(`Comment item for student ${student.id}:`, commentItem);
              
              if (commentItem && commentItem.optionName) {
                const commentText = commentItem.optionName || '';
                console.log(`Setting comment for student ${student.id}: "${commentText}"`);
                // Set comment text
                newComments[student.id] = commentText;
                
                // Store IDs for updating (including detail record ID if it exists)
                newCommentIds[student.id] = {
                  optionID: commentItem.optionID || null,
                  categoryID: commentItem.categoryID || null,
                  detailId: commentItem.id || null, // Store detail record ID for updates
                };
                console.log(`Stored detail record ID for student ${student.id}:`, commentItem.id);
              }
            }
          });
          
          // Replace the entire objects to ensure reactivity
          this.studentComments = newComments;
          this.studentCommentIds = newCommentIds;
          
          // Debug: Log final studentComments with actual values
          console.log('Final studentComments after extraction:', JSON.parse(JSON.stringify(this.studentComments)));
          console.log('Final studentCommentIds after extraction:', JSON.parse(JSON.stringify(this.studentCommentIds)));
          console.log('Checking if comments are in studentComments:', {
            student16758: this.studentComments[16758],
            student16915: this.studentComments[16915],
            allKeys: Object.keys(this.studentComments)
          });
        }
      } catch (error) {
        console.error("Error loading students:", error);
        this.$bvToast.toast("Failed to load students", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.studentsData = [];
      } finally {
        this.studentsLoading = false;
      }
    },
    async loadExamTypes() {
      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const url = `${this.$store.state.domain}ExamTypes/LoadData?db=${dbName}&cID=${cID}&status=active&page=1&pageSize=1000`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const data =
          (response && response.data && Array.isArray(response.data)
            ? response.data
            : null) || (Array.isArray(response) ? response : []);
        this.examTypes = data;
      } catch (error) {
        console.error("Error loading exam types:", error);
        this.examTypes = [];
      }
    },
    async loadSessions() {
      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const url = `${this.$store.state.domain}Sessions?db=${dbName}&cID=${cID}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const data =
          (response && response.data && Array.isArray(response.data)
            ? response.data
            : null) || (Array.isArray(response) ? response : []);
        this.sessions = data;
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    getDefaultAvatar(gender) {
      // Return default avatar based on gender
      return null; // You can add default avatar URLs here
    },
    getSubjectNameById(id) {
      const found = this.subjectOptions.find((opt) => opt.value === id);
      return found ? found.label : "";
    },
    getExamTypeNameById(id) {
      const found = this.examTypes.find((opt) => opt.id === id);
      return found ? found.examType : "";
    },
    findExamTypeIdByName(name) {
      const target = String(name || "")
        .trim()
        .toUpperCase();
      const found = this.examTypes.find(
        (opt) =>
          String(opt.examType || "")
            .trim()
            .toUpperCase() === target
      );
      return found ? found.id : null;
    },
    filterPreviewMarksRows(data) {
      if (!Array.isArray(data)) return [];
      const hiddenSubjects = ["COMMENT", "PROMOTION TAG"];
      return data.filter((row) => {
        const name = (row.subjectName || "").trim().toUpperCase();
        return !hiddenSubjects.includes(name);
      });
    },
    getSessionNameById(id) {
      const found = this.sessions.find((opt) => opt.id === id);
      return found ? found.session : "";
    },
    getTypeLabelByValue(val) {
      const found = this.typeOptions.find((opt) => opt.value === val);
      return found ? found.label || found.text || found.value : val || "";
    },
    async viewStudentDetails(student) {
      if (!this.canOpenStudent) {
        this.$bvToast.toast(
          `Please select ${this.missingSelectionsLabel} before opening marks entry.`,
          {
            title: "Selection Required",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      // Set loading state for this student's button
      this.loadingStudentId = student.id;

      // Check if marks already exist for this student
      let existingMarksData = null;
      let marksRecordId = null;
      
      try {
        const userLinkID = this.$store.state.userData.userLinkID || this.$store.state.userData.userID || this.$store.state.userData.localID || 0;
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          sID: student.id,
          examTypeID: this.selectedExamType || '',
          sessionID: this.selectedSession || '',
          classID: this.selectedClass || '',
          subjectID: this.selectedSubject || '',
          sectionID: this.selectedSection || 0,
          type: this.selectedType || '',
        });

        const checkUrl = `${this.$store.state.domain}TabulationSheetMarks/GetByStudentAndFilters?${params.toString()}`;
        const checkResponse = await this.get({
          url: checkUrl,
          token: this.$store.state.userData.token,
        });

        // Check if marks exist
        if (checkResponse) {
          let marksData = null;
          if (checkResponse.data && checkResponse.data.id) {
            marksData = checkResponse.data;
            marksRecordId = checkResponse.data.id;
          } else if (checkResponse.id) {
            marksData = checkResponse;
            marksRecordId = checkResponse.id;
          } else if (Array.isArray(checkResponse.data) && checkResponse.data.length > 0) {
            marksData = checkResponse.data[0];
            marksRecordId = checkResponse.data[0].id;
          } else if (Array.isArray(checkResponse) && checkResponse.length > 0) {
            marksData = checkResponse[0];
            marksRecordId = checkResponse[0].id;
          }

          if (marksData) {
            existingMarksData = marksData;
          }
        }
      } catch (error) {
        console.error('Error checking existing marks:', error);
        // Continue even if check fails
      } finally {
        // Clear loading state
        this.loadingStudentId = null;
      }

      // Get all student IDs and current student index for Save & Next functionality
      const studentIds = this.sortedStudents.map(s => s.id).join(',');
      const currentIndex = this.sortedStudents.findIndex(s => s.id === student.id);
      
      // Validate currentIndex - should be >= 0
      if (currentIndex < 0) {
        console.error('Student not found in sortedStudents list:', student.id);
        this.$bvToast.toast('Error: Student not found in list', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }
      
      console.log('Navigating to student:', {
        studentId: student.id,
        studentName: student.name,
        currentIndex: currentIndex,
        totalStudents: this.sortedStudents.length,
        studentIds: studentIds
      });

      // Navigate with marks data if exists
      this.$router.push({
        name: "marks-entry-student",
        query: {
          studentId: student.id,
          studentName: student.name,
          grNo: student.gr || "",
          fatherName: student.fatherName || "",
          classId: this.selectedClass,
          className: this.selectedClassName,
          subjectId: this.selectedSubject,
          subjectName: this.getSubjectNameById(this.selectedSubject),
          sectionId: this.selectedSection || 0,
          sectionName: student.section || this.selectedSectionName,
          examTypeId: this.selectedExamType,
          examTypeName: this.getExamTypeNameById(this.selectedExamType),
          sessionId: this.selectedSession,
          sessionName: this.getSessionNameById(this.selectedSession),
          type: this.selectedType,
          typeLabel: this.getTypeLabelByValue(this.selectedType),
          marksRecordId: marksRecordId || null,
          hasExistingMarks: existingMarksData ? 'true' : 'false',
          tID: this.staffID || null, // Pass the teacher ID
          studentIds: studentIds, // Pass all student IDs for Save & Next
          currentIndex: currentIndex.toString(), // Explicitly convert to string for query param
        },
      });
    },
    openMarksEntry(student) {
      // Navigate to marks entry page with student and subject details
      this.$router.push({
        name: "manage-marks",
        query: {
          studentID: student.id,
          classID: this.selectedClass,
          sectionID: this.selectedSection,
          subjectID: this.selectedSubject,
          className: this.selectedClassName,
          sectionName: this.selectedSectionName,
        },
      });
    },
    toggleCommentArea(studentId) {
      const isExpanded = this.expandedComments[studentId];
      this.$set(this.expandedComments, studentId, !isExpanded);
      
      // Toggle row details using b-table's built-in mechanism
      const student = this.sortedStudents.find(s => s.id === studentId);
      if (student) {
        this.$set(student, '_showDetails', !isExpanded);
      }
    },
    openCommentModal(studentId) {
      this.commentModalStudentId = studentId;
      this.commentSearchQuery = "";
      this.commentCurrentPage = 1;
      this.loadCommentBank();
      this.$bvModal.show("comment-selection-modal");
    },
    closeCommentModal() {
      this.commentModalStudentId = null;
      this.commentSearchQuery = "";
      this.commentBankComments = [];
      this.filteredComments = [];
    },
    async loadCommentBank() {
      this.loadingComments = true;
      try {
        const dbName = this.$store.state.userData.db; // Don't encode, URLSearchParams will handle it
        const cID = this.$store.state.userData.cId;
        const search = this.commentSearchQuery?.trim() || "";

        // Build URL with proper parameters matching the API format
        const params = new URLSearchParams({
          db: dbName,
          campusID: cID,
          page: this.commentCurrentPage,
          pageSize: this.commentPerPage,
        });

        // Add search parameter if provided
        if (search) {
          params.append("search", search);
        }

        const url = `${this.$store.state.domain}RemarksExtra/GetByPagination?${params.toString()}`;
        
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        let comments = [];
        let totalCount = 0;

        // Handle different response structures
        if (response) {
          if (response.status === "success" && Array.isArray(response.data)) {
            comments = response.data;
            totalCount = response.totalCount || response.total || 0;
          } else if (Array.isArray(response.data)) {
            comments = response.data;
            totalCount = response.total || response.totalCount || response.data.length;
          } else if (Array.isArray(response)) {
            comments = response;
            totalCount = response.length;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            comments = response.data.data;
            totalCount = response.data.totalCount || response.data.total || 0;
          }
        }

        this.commentBankComments = comments;
        this.filteredComments = comments;
        this.commentTotalCount = totalCount;
      } catch (error) {
        console.error("Error loading comment bank:", error);
        this.$bvToast.toast("Failed to load comments", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.commentBankComments = [];
        this.filteredComments = [];
        this.commentTotalCount = 0;
      } finally {
        this.loadingComments = false;
      }
    },
    onCommentSearchChange() {
      // Clear existing timeout
      if (this.commentSearchTimeout) {
        clearTimeout(this.commentSearchTimeout);
      }
      
      // Reset to first page when searching
      this.commentCurrentPage = 1;
      
      // Debounce the API call
      this.commentSearchTimeout = setTimeout(() => {
        this.loadCommentBank();
      }, 300);
    },
    clearCommentSearch() {
      this.commentSearchQuery = "";
      this.commentCurrentPage = 1;
      this.loadCommentBank();
    },
    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    onCommentPageChange(page) {
      this.commentCurrentPage = page;
      this.loadCommentBank();
    },
    selectComment(comment) {
      if (this.commentModalStudentId) {
        const commentText = comment.remarks || comment.comment || "";
        this.$set(this.studentComments, this.commentModalStudentId, commentText);
        // Clear IDs when selecting a new comment from bank (treat as new comment)
        this.$set(this.studentCommentIds, this.commentModalStudentId, {
          optionID: null,
          categoryID: null,
          detailId: null, // Clear detail record ID when selecting new comment
        });
        this.$bvModal.hide("comment-selection-modal");
        this.$bvToast.toast("Comment selected successfully", {
          title: this.$t("Success"),
          variant: "success",
          solid: true,
        });
      }
    },
    async loadComments() {
      if (!this.isCommentSubject || !this.selectedClass || this.selectedSection === null) {
        this.existingComments = [];
        this.studentComments = {};
        this.studentCommentIds = {};
        return;
      }

      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        
        // Build query parameters
        const params = new URLSearchParams({
          db: dbName,
          cID: cID,
          classID: this.selectedClass,
          sectionID: this.selectedSection || 0,
          subjectID: this.selectedSubject,
        });

        if (this.selectedExamType) {
          params.append('examTypeID', this.selectedExamType);
        }
        if (this.selectedSession) {
          params.append('sessionID', this.selectedSession);
        }

        const url = `${this.$store.state.domain}RemarksExtra?${params.toString()}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        // Parse response
        let comments = [];
        if (response) {
          if (Array.isArray(response)) {
            comments = response;
          } else if (response.data && Array.isArray(response.data)) {
            comments = response.data;
          } else if (response.status === 'success' && Array.isArray(response.data)) {
            comments = response.data;
          }
        }

        this.existingComments = comments;
        
        // Map comments to studentComments object
        const commentsMap = {};
        comments.forEach(comment => {
          if (comment.studentID || comment.sID) {
            const studentId = comment.studentID || comment.sID;
            commentsMap[studentId] = comment.remarks || comment.comment || '';
          }
        });
        
        // Initialize studentComments with existing comments or empty strings for all students
        this.sortedStudents.forEach(student => {
          this.$set(this.studentComments, student.id, commentsMap[student.id] || '');
          // Don't auto-expand - let user click to expand
          // Initialize as collapsed (false) if not already set
          if (this.expandedComments[student.id] === undefined) {
            this.$set(this.expandedComments, student.id, false);
          }
          // Set _showDetails based on expandedComments state
          this.$set(student, '_showDetails', this.expandedComments[student.id] === true);
        });
      } catch (error) {
        console.error('Error loading comments:', error);
        this.existingComments = [];
        this.studentComments = {};
        this.studentCommentIds = {};
      }
    },
    async getOrCreateTabulationSheet() {
      try {
        const dbName = this.$store.state.userData.db;
        const cID = this.$store.state.userData.cId;
        const userLinkID = this.$store.state.userData.userID || this.$store.state.userData.localID || 0;

        // First, try to get existing tabulation sheet for comment type
        const params = new URLSearchParams({
          db: dbName,
          cID: cID,
          classId: this.selectedClass,
          sectionId: this.selectedSection || 0,
          subjectId: this.selectedSubject,
          type: 'comment',
        });

        if (this.selectedExamType) {
          params.append('examTypeId', this.selectedExamType);
        }
        if (this.selectedSession) {
          params.append('sessionId', this.selectedSession);
        }

        const getUrl = `${this.$store.state.domain}TabulationSheet/GetByPagination?${params.toString()}&page=1&pageSize=1`;
        const getResponse = await this.get({
          url: getUrl,
          token: this.$store.state.userData.token,
        });

        let tabulationSheetId = null;
        if (getResponse && getResponse.data && Array.isArray(getResponse.data) && getResponse.data.length > 0) {
          tabulationSheetId = getResponse.data[0].id;
        } else if (getResponse && Array.isArray(getResponse) && getResponse.length > 0) {
          tabulationSheetId = getResponse[0].id;
        }

        // If no existing sheet found, create a new one
        if (!tabulationSheetId) {
          const createPayload = {
            id: 0,
            classID: this.selectedClass,
            sectionID: this.selectedSection || 0,
            subjectID: this.selectedSubject,
            examTypeID: this.selectedExamType || null,
            sessionID: this.selectedSession || null,
            type: 'comment',
            name: `Comment - ${this.selectedClassName}`,
            categories: [],
            questions: [],
            records: [],
          };

          const createUrl = `${this.$store.state.domain}TabulationSheet/Save?db=${dbName}&cID=${cID}&userID=${userLinkID}`;
          const createResponse = await this.post({
            url: createUrl,
            body: createPayload,
            token: this.$store.state.userData.token,
          });

          if (createResponse && createResponse.id) {
            tabulationSheetId = createResponse.id;
          } else if (createResponse && createResponse.data && createResponse.data.id) {
            tabulationSheetId = createResponse.data.id;
          }
        }

        return tabulationSheetId;
      } catch (error) {
        console.error('Error getting/creating tabulation sheet:', error);
        return null;
      }
    },
    async saveAllComments() {
      if (!this.isCommentSubject || !this.selectedClass || this.selectedSection === null) {
        return;
      }

      this.savingComments = true;
      try {
        const dbName = this.$store.state.userData.db;
        const cID = this.$store.state.userData.cId;
        const userLinkID = this.$store.state.userData.userID || this.$store.state.userData.localID || 0;

        // Get or create tabulation sheet for comments
        const tabulationSheetID = await this.getOrCreateTabulationSheet();
        if (!tabulationSheetID) {
          this.$bvToast.toast('Failed to get or create tabulation sheet', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
          return;
        }

        // Prepare students array with comments
        const students = [];
        
        // Debug: Log all studentCommentIds before building payload
        console.log('All studentCommentIds before building payload:', JSON.parse(JSON.stringify(this.studentCommentIds)));
        
        this.sortedStudents.forEach(student => {
          const commentText = (this.studentComments[student.id] || '').trim();
          
          // Only include students with comments
          if (commentText) {
            // Get comment IDs for this student
            const commentIds = this.studentCommentIds[student.id] || {};
            
            console.log(`Processing student ${student.id}:`, {
              commentText,
              commentIds,
              hasDetailId: !!commentIds.detailId,
              detailIdValue: commentIds.detailId
            });
            
            // Build marksData item
            const marksDataItem = {
              categoryID: this.selectedSubject, // Comment identifier (subjectID)
              optionName: commentText, // Comment/remark text
              rating: "", // Optional metadata
            };
            
            // Include detail record ID if it exists (for updates)
            // Only include id if comment was previously saved (has detailId)
            if (commentIds && commentIds.detailId) {
              marksDataItem.id = commentIds.detailId;
              console.log(`✓ Including detail record ID ${commentIds.detailId} for student ${student.id} (update)`);
            } else {
              console.log(`✗ No detail record ID for student ${student.id} (new comment). commentIds:`, commentIds);
            }

            students.push({
              sID: student.id,
              status: "P", // Present status
              marksData: [marksDataItem],
            });
          }
        });

        if (students.length === 0) {
          this.$bvToast.toast('No comments to save', {
            title: 'Info',
            variant: 'info',
            solid: true,
          });
          return;
        }

        // Build payload according to API structure
        const payload = {
          id: 0, // 0 for new, will be updated if existing record found
          type: "comment",
          tabulationSheetID: tabulationSheetID,
          subjectID: this.selectedSubject,
          classID: this.selectedClass,
          sectionID: this.selectedSection || 0,
          examTypeID: this.selectedExamType || null,
          sessionID: this.selectedSession || null,
          students: students,
        };

        // Check if there's an existing marks record to update
        if (students.length > 0) {
          try {
            const checkParams = new URLSearchParams({
              db: dbName,
              cID: cID,
              classID: this.selectedClass,
              sectionID: this.selectedSection || 0,
              subjectID: this.selectedSubject,
              type: 'comment',
            });

            if (this.selectedExamType) {
              checkParams.append('examTypeID', this.selectedExamType);
            }
            if (this.selectedSession) {
              checkParams.append('sessionID', this.selectedSession);
            }

            // Try to get existing record for first student to check if update is needed
            const checkUrl = `${this.$store.state.domain}TabulationSheetMarks/GetByStudentAndFilters?${checkParams.toString()}&sID=${students[0].sID}`;
            const checkResponse = await this.get({
              url: checkUrl,
              token: this.$store.state.userData.token,
            });

            if (checkResponse && checkResponse.id) {
              payload.id = checkResponse.id;
            } else if (checkResponse && checkResponse.data && checkResponse.data.id) {
              payload.id = checkResponse.data.id;
            }
          } catch (error) {
            console.log('No existing record found, will create new one');
          }
        }

        // Log payload for debugging
        console.log('Saving comments payload:', JSON.stringify(payload, null, 2));
        console.log('Students with detail IDs:', students.map(s => ({
          sID: s.sID,
          hasDetailId: s.marksData[0]?.id ? true : false,
          detailId: s.marksData[0]?.id || null
        })));

        // Save using TabulationSheetMarks/Save API
        const saveUrl = `${this.$store.state.domain}TabulationSheetMarks/Save?db=${dbName}&cID=${cID}&userID=${userLinkID}`;
        const response = await this.post({
          url: saveUrl,
          body: payload,
          token: this.$store.state.userData.token,
          message: `Comments saved successfully for ${students.length} student(s)`,
          context: this,
        });

        if (response) {
          // Update comment IDs from response if available
          if (response.data && response.data.students && Array.isArray(response.data.students)) {
            response.data.students.forEach((savedStudent) => {
              if (savedStudent.marksData && Array.isArray(savedStudent.marksData) && savedStudent.marksData.length > 0) {
                const commentItem = savedStudent.marksData[0];
                if (commentItem && savedStudent.sID) {
                  this.$set(this.studentCommentIds, savedStudent.sID, {
                    optionID: commentItem.optionID || null,
                    categoryID: commentItem.categoryID || this.selectedSubject,
                    detailId: commentItem.id || null, // Store detail record ID from response
                  });
                  console.log(`Updated comment IDs for student ${savedStudent.sID} with detailId:`, commentItem.id);
                }
              }
            });
          }
          
          // Reload students to update marksSaved status and get fresh commentData
          if (this.selectedClass && this.selectedSection !== null) {
            await this.loadStudents();
          }
        }
      } catch (error) {
        console.error('Error saving comments:', error);
        this.$bvToast.toast('Failed to save comments: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.savingComments = false;
      }
    },
    openPreviewMarksModal(studentId) {
      this.previewMarksModalStudentId = studentId;
      this.loadPreviewMarks(studentId);
      this.$bvModal.show("preview-marks-modal");
    },
    closePreviewMarksModal() {
      this.previewMarksModalStudentId = null;
      this.previewMarksData = [];
      this.previewFinalTermAssessmentMarks = [];
      this.previewCommentsByExamType = [];
    },
    /** Normalize remark text for preview (placeholder-only → em dash) */
    formatPreviewRemarkBody(text) {
      const t = text == null ? "" : String(text).trim();
      if (t === "" || t === "-") return "—";
      return t;
    },
    parsePreviewCommentsByExamType(response, studentId) {
      const sid = Number(studentId);
      const raw =
        (response && Array.isArray(response.commentsByExamType) && response.commentsByExamType) ||
        (response &&
          response.data &&
          Array.isArray(response.data.commentsByExamType) &&
          response.data.commentsByExamType) ||
        [];
      if (!raw.length || Number.isNaN(sid)) return [];

      return raw
        .map((block, idx) => {
          const comments = (block.comments || []).filter((c) => {
            const csid = c.sID != null ? Number(c.sID) : NaN;
            return !Number.isNaN(csid) && csid === sid;
          });
          const examTypeID = block.examTypeID != null ? block.examTypeID : idx;
          return {
            examTypeKey: `${examTypeID}-${idx}`,
            examTypeID: block.examTypeID,
            examTypeName: block.examTypeName || "Exam",
            comments,
          };
        })
        .filter((block) => block.comments.length > 0);
    },
    parsePreviewMarksPayload(response) {
      let marksData = [];
      const payload = response && typeof response === "object" ? response : null;
      if (payload) {
        if (payload.status === "success" && Array.isArray(payload.data)) {
          marksData = payload.data;
        } else if (Array.isArray(payload.data)) {
          marksData = payload.data;
        } else if (Array.isArray(payload)) {
          marksData = payload;
        }
      }
      return { marksData, payload };
    },
    async fetchPreviewMarksPayload(studentId, examTypeID) {
      const dbName = this.$store.state.userData.db;
      const cID = this.$store.state.userData.cId;

      const params = new URLSearchParams({
        db: dbName,
        cID: cID,
        page: 1,
        pageSize: 100,
        sID: studentId,
        allData: "true",
      });

      if (this.selectedClass) {
        params.append("classID", this.selectedClass);
      }
      if (examTypeID) {
        params.append("examTypeID", examTypeID);
      }
      if (this.selectedSession) {
        params.append("sessionID", this.selectedSession);
      }

      const url = `${this.$store.state.domain}TabulationSheetMarks/GetAllMarksData?${params.toString()}`;
      const response = await this.get({
        url,
        token: this.$store.state.userData.token,
        returnData: true,
      });

      return this.parsePreviewMarksPayload(response);
    },
    async loadPreviewMarks(studentId) {
      if (!studentId) return;

      this.loadingMarks = true;
      this.previewCommentsByExamType = [];
      this.previewFinalTermAssessmentMarks = [];
      try {
        const { marksData, payload } = await this.fetchPreviewMarksPayload(
          studentId,
          this.selectedExamType
        );

        this.previewMarksData = marksData;
        this.previewCommentsByExamType = this.parsePreviewCommentsByExamType(
          payload,
          studentId
        );

        if (this.isFinalTermAggregateSelected && this.finalTermAssessmentExamTypeId) {
          const { marksData: ftaMarks } = await this.fetchPreviewMarksPayload(
            studentId,
            this.finalTermAssessmentExamTypeId
          );
          this.previewFinalTermAssessmentMarks = ftaMarks;
        }
      } catch (error) {
        console.error("Error loading preview marks:", error);
        this.$bvToast.toast("Failed to load marks preview", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.previewMarksData = [];
        this.previewFinalTermAssessmentMarks = [];
        this.previewCommentsByExamType = [];
      } finally {
        this.loadingMarks = false;
      }
    },
    getMarksColorClass(item) {
      if (item.status === 'PASSED') return 'text-success';
      if (item.status === 'FAIL') return 'text-danger';
      return 'text-muted';
    },
    getPercentageVariant(percentage) {
      if (percentage === null || percentage === undefined) return 'secondary';
      if (percentage >= 90) return 'success';
      if (percentage >= 80) return 'info';
      if (percentage >= 70) return 'primary';
      if (percentage >= 60) return 'warning';
      return 'danger';
    },
    getGradeVariant(grade) {
      if (!grade || grade === '-') return 'secondary';
      if (grade === 'A' || grade === 'A+') return 'success';
      if (grade === 'B' || grade === 'B+') return 'info';
      if (grade === 'C' || grade === 'C+') return 'primary';
      if (grade === 'D') return 'warning';
      return 'danger';
    },
    saveFilters() {
      const filters = {
        selectedClass: this.selectedClass,
        selectedSection: this.selectedSection,
        selectedSubject: this.selectedSubject,
        selectedExamType: this.selectedExamType,
        selectedSession: this.selectedSession,
        selectedType: this.selectedType,
        selectedClassName: this.selectedClassName,
        selectedSectionName: this.selectedSectionName,
      };
      localStorage.setItem("marksEntryFilters", JSON.stringify(filters));
    },
    async restoreFilters() {
      try {
        const savedFilters = localStorage.getItem("marksEntryFilters");
        if (savedFilters) {
          const filters = JSON.parse(savedFilters);
          
          // Temporarily disable watchers to avoid saving during restoration
          this.isRestoringFilters = true;
          
          // Restore basic filter values
          if (filters.selectedClass && this.classOptions.length > 0) {
            const classExists = this.classOptions.some(
              (opt) => opt.value === filters.selectedClass
            );
            if (classExists) {
              // Use onClassChange to properly set up sections and subjects
              await this.onClassChange(filters.selectedClass);
              
              // Restore class name if it was saved
              if (filters.selectedClassName) {
                this.selectedClassName = filters.selectedClassName;
              }
              
              // Restore section after class is set
              await this.$nextTick();
              if (filters.selectedSection !== null && filters.selectedSection !== undefined) {
                const sectionExists = this.sectionOptions.some(
                  (opt) => opt.value === filters.selectedSection
                );
                if (sectionExists) {
                  // Use onSectionChange to properly set up subjects and load students
                  await this.onSectionChange(filters.selectedSection);
                  
                  // Restore section name if it was saved
                  if (filters.selectedSectionName) {
                    this.selectedSectionName = filters.selectedSectionName;
                  }
                  
                  // Restore subject after section is set
                  await this.$nextTick();
                  if (filters.selectedSubject && this.subjectOptions.length > 0) {
                    const subjectExists = this.subjectOptions.some(
                      (opt) => opt.value === filters.selectedSubject
                    );
                    if (subjectExists) {
                      this.selectedSubject = filters.selectedSubject;
                    }
                  }
                }
              }
            }
          }
          
          // Restore exam type
          if (filters.selectedExamType && this.examTypes.length > 0) {
            const examTypeExists = this.examTypes.some(
              (opt) => opt.id === filters.selectedExamType
            );
            if (examTypeExists) {
              this.selectedExamType = filters.selectedExamType;
            }
          }
          
          // Restore session
          if (filters.selectedSession && this.sessions.length > 0) {
            const sessionExists = this.sessions.some(
              (opt) => opt.id === filters.selectedSession
            );
            if (sessionExists) {
              this.selectedSession = filters.selectedSession;
            }
          }
          
          // Restore type (check against filtered options)
          if (filters.selectedType) {
            await this.$nextTick(); // Wait for filteredTypeOptions to update
            const typeExists = this.filteredTypeOptions.some(
              (opt) => opt.value === filters.selectedType
            );
            if (typeExists) {
              this.selectedType = filters.selectedType;
            } else {
              // If the saved type is not available for current class, clear it
              this.selectedType = null;
            }
          }
          
          // Re-enable watchers
          this.isRestoringFilters = false;
          
          // Save the restored filters
          this.saveFilters();
        }
      } catch (error) {
        console.error("Error restoring filters:", error);
        this.isRestoringFilters = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Keyframe animations */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


.marks-entry-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem; // More left padding, less right padding
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible; // Changed from hidden to visible for dropdown menus
  animation: slideUpFade 0.4s ease-out both;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    z-index: 1;
    border-radius: 10px 10px 0 0;
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  ::v-deep .v-select {
    position: relative;
    z-index: 10;
  }

  ::v-deep .vs__dropdown-toggle {
    border-radius: 12px;
    min-height: 48px;
    border: 2px solid #e0d8ff;
    background: #fff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08);
    position: relative;
    z-index: 10;

    &:hover {
      border-color: #b8a9ff;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.12);
    }
  }

  ::v-deep .vs__dropdown-menu {
    border-radius: 12px;
    border: 2px solid #e0d8ff;
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
    background: #ffffff !important;
    z-index: 9999 !important;
    position: fixed !important;
    margin-top: 4px;
    max-height: 300px;
    overflow-y: auto;
    min-width: 200px;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.75rem 1rem;
    color: #2d1b69 !important;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
      opacity: 1 !important;
    }

    &--highlight {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600;
      opacity: 1 !important;
    }
  }

  ::v-deep .vs__selected {
    color: #2d1b69 !important;
    font-weight: 600;
    background: transparent !important;
  }

  ::v-deep .vs__search {
    color: #2d1b69 !important;
    font-weight: 500;
    padding: 0.5rem;
    background: transparent !important;
  }

  ::v-deep .vs__clear {
    color: #6b7280;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #7c5dfa;
    }
  }

  label {
    font-weight: 700;
    color: #2d1b69;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
  }
}

.filter-row {
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-left: -0.375rem;
  margin-right: -0.375rem;
  display: flex;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
    gap: 0.75rem;
    margin-left: 0;
    margin-right: 0;
    justify-content: space-between;
  }

  > [class*="col-"] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    flex: 1 1 auto;
    
    @media (min-width: 1200px) {
      flex: 1 1 0;
      padding-left: 0;
      padding-right: 0;
      margin-right: 0;
      min-width: 0;
    }
  }
  
  .section-col {
    @media (min-width: 1200px) {
      flex: 0 0 auto;
      max-width: 140px;
    }
  }
  
  .exam-type-col {
    @media (min-width: 1200px) {
      flex: 0 0 auto;
      min-width: 280px;
    }
  }

  .b-form-group {
    margin-bottom: 0;
    
    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #5b21b6;
      margin-bottom: 0.5rem;
      display: block;
      text-align: left;
    }
  }

  .v-select {
    width: 100%;
    
    .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.5rem 0.75rem;
      min-height: 42px;
      background: #fff;
      transition: all 0.2s ease;
      
      &:hover {
        border-color: #b8a9ff;
      }
      
      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    ::v-deep .vs__dropdown-menu {
      border-radius: 12px;
      border: 2px solid #e0d8ff;
      box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
      background: #ffffff !important;
      z-index: 9999 !important;
      position: fixed !important;
      margin-top: 4px;
      max-height: 300px;
      overflow-y: auto;
      min-width: 200px;
    }

    ::v-deep .vs__dropdown-option {
      padding: 0.75rem 1rem;
      color: #2d1b69 !important;
      font-weight: 500;
      transition: all 0.2s ease;
      cursor: pointer;
      background-color: #ffffff !important;
      background-image: none !important;
      opacity: 1 !important;
      visibility: visible !important;

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
        opacity: 1 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600;
        opacity: 1 !important;
      }
    }
    
    .vs__selected {
      margin: 0;
      padding: 0;
      border: none;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;
    }
    
    .vs__search {
      margin: 0;
      padding: 0;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;
      
      &::placeholder {
        color: #9ca3af;
        font-weight: 400;
      }
    }
  }
}

.section-col {
  min-width: 120px;

  @media (min-width: 1200px) {
    flex: 0 0 14%;
    max-width: 14%;
  }

  ::v-deep .vs__dropdown-toggle {
    min-width: 110px;
    max-width: 130px;
  }
  
  ::v-deep .v-select {
    max-width: 130px;
  }
}

.exam-type-col {
  ::v-deep .vs__dropdown-toggle {
    min-width: 280px;
    max-height: 48px !important;
    height: 48px !important;
    overflow: hidden !important;
    padding: 0.5rem 0.75rem !important;
  }
  
  ::v-deep .vs__selected-options {
    display: flex !important;
    flex-wrap: nowrap !important;
    overflow: hidden !important;
    max-width: calc(100% - 60px) !important;
    align-items: center !important;
    height: 100% !important;
  }
  
  ::v-deep .vs__selected {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: fit-content !important;
    display: inline-block !important;
    margin-right: 0.5rem !important;
    flex-shrink: 0 !important;
    
    &::after {
      content: ', ' !important;
      margin-right: 0.25rem !important;
    }
    
    &:last-child::after {
      content: '' !important;
    }
  }
  
  ::v-deep .vs__actions {
    flex-shrink: 0 !important;
    width: auto !important;
  }
  
  ::v-deep .vs__clear,
  ::v-deep .vs__open-indicator {
    flex-shrink: 0 !important;
  }
}

.students-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-top: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.4s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    border-radius: 10px 10px 0 0;
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
}

.students-list-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.sort-select-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-filter-wrapper {
  margin-right: 0.5rem;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  min-height: 42px;
  transition: all 0.2s ease;
  min-width: 250px;

  &:focus-within {
    border-color: #7c5dfa;
    background: white;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }

  .search-icon {
    color: #9ca3af;
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  .search-input {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 0.9rem;
    flex: 1;
    outline: none;
    color: #2d1b69;
    font-weight: 500;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }

    &:focus {
      box-shadow: none;
    }
  }

  .clear-search-btn {
    padding: 0.25rem;
    color: #9ca3af;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.5rem;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      color: #7c5dfa;
      background: #f3f4f6;
      border-radius: 6px;
    }
  }
}

.sort-select-wrapper {
  .sort-select {
    width: 100%;
    min-width: 160px;
    
    ::v-deep .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.5rem 0.75rem;
      min-height: 42px;
      background: #fff;
      transition: all 0.2s ease;
      
      &:hover {
        border-color: #b8a9ff;
      }
      
      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    ::v-deep .vs__dropdown-menu {
      border-radius: 12px;
      border: 2px solid #e0d8ff;
      box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
      background: #ffffff !important;
      z-index: 9999 !important;
      position: fixed !important;
      margin-top: 4px;
      max-height: 300px;
      overflow-y: auto;
      min-width: 200px;
    }

    ::v-deep .vs__dropdown-option {
      padding: 0.75rem 1rem;
      color: #2d1b69 !important;
      font-weight: 500;
      transition: all 0.2s ease;
      cursor: pointer;
      background-color: #ffffff !important;
      background-image: none !important;
      opacity: 1 !important;
      visibility: visible !important;

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
        opacity: 1 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600;
        opacity: 1 !important;
      }
    }
    
    ::v-deep .vs__selected {
      margin: 0;
      padding: 0;
      border: none;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;
    }
    
    ::v-deep .vs__search {
      margin: 0;
      padding: 0;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;
      
      &::placeholder {
        color: #9ca3af;
        font-weight: 400;
      }
    }
  }
}

.students-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;

  ::v-deep th {
    background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
    border: none;
    font-weight: 700;
    color: #1f2937;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.8px;
    padding: 1.25rem 1rem;
    border-bottom: 2px solid #e5e7eb;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent 0%, #d1d5db 50%, transparent 100%);
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f3f4f6;
    border-bottom: 1px solid #f9fafb;
    padding: 1.25rem 1rem;
    background: #ffffff;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // Row details cell styling
  ::v-deep tbody tr[class*="b-table-details"] {
    td {
      padding: 0;
      border-top: none;
      border-bottom: 2px solid #e0d8ff;
      background: transparent;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    border-left: 3px solid transparent;

    &:nth-child(even) {
      background: #fafafa;
      
      td {
        background: #fafafa;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;
      
      td {
        background: #ffffff;
      }
    }

    // Highlight rows with fully saved marks (green)
    &.marks-saved-row {
      background: linear-gradient(90deg, #f0fdf4 0%, #ffffff 50%, #f9fafb 100%) !important;
      border-left-color: #10b981;
      
      td {
        background: transparent !important;
      }
      
      &:hover {
        background: linear-gradient(90deg, #d1fae5 0%, #f0fdf4 50%, #ecfdf5 100%) !important;
        border-left-color: #059669;
      }
    }

    // Highlight rows with partially saved marks (dark yellow)
    &.marks-partial-row {
      background: linear-gradient(90deg, #fef3c7 0%, #fffbeb 40%, #fef3c7 100%) !important;
      border-left-color: #d97706;
      
      td {
        background: transparent !important;
      }
      
      &:hover {
        background: linear-gradient(90deg, #fde68a 0%, #fef3c7 40%, #fde68a 100%) !important;
        border-left-color: #b45309;
      }
    }

    &:hover {
      background: linear-gradient(90deg, #f3f4f6 0%, #ffffff 50%, #f9fafb 100%) !important;
      border-left-color: #7c5dfa;
      transform: translateX(4px);
      box-shadow: -4px 0 12px rgba(124, 93, 250, 0.15), 0 2px 8px rgba(0, 0, 0, 0.05);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        border-top-color: #e5e7eb;
        border-bottom-color: #e5e7eb;
      }

      .student-avatar {
        box-shadow: 0 4px 16px rgba(124, 93, 250, 0.25);
        border-color: #7c5dfa;
      }

      .name-text {
        color: #7c5dfa;
        transform: translateX(2px);
      }

      .action-pill {
        transform: translateX(2px);
        box-shadow: 0 4px 16px rgba(124, 93, 250, 0.3);
      }

      .action-plus-btn {
        transform: scale(1.1) rotate(90deg);
        box-shadow: 0 6px 20px rgba(124, 93, 250, 0.4);
      }
    }

    &:active {
      transform: translateX(2px);
    }
  }

  ::v-deep .avatar {
    border: 2px solid #e5e7eb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: #f9fafb;
  }

  ::v-deep .student-avatar {
    border: 2px solid #e5e7eb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: #f9fafb;
  }

  // Make the inner user icon visible on partial (yellow) rows
  ::v-deep .student-avatar-icon-partial {
    color: #92400e !important;
  }

  ::v-deep small {
    font-size: 0.8rem;
    color: #6b7280;
    font-weight: 600;
    margin-top: 0.25rem;
    display: block;
  }
}

.empty-state-box {
  padding: 4rem 2rem;
  text-align: center;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
  margin: 2rem 0;
  animation: slideUpFade 0.5s ease-out both;
}

.empty-state-content {
  max-width: 500px;
  margin: 0 auto;
}

.empty-state-icon {
  margin-bottom: 1.5rem;
  color: #9ca3af;
  
  svg {
    stroke-width: 1.5;
  }
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.empty-state-submessage {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 400;
  line-height: 1.5;
}

.sno-col {
  width: 70px;
  text-align: center;
}

.name-text {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d1b69;
}

.action-pill {
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 999px;
  padding: 0.4rem 1rem;

  &:hover:not(:disabled) {
    transform: translateX(2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.action-plus-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  justify-content: center;
  padding: 0;
  border: none;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    transform: scale(1.1) rotate(90deg);
    box-shadow: 0 6px 20px rgba(124, 93, 250, 0.4);
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6ff5 100%);
  }

  &:active:not(:disabled) {
    transform: scale(0.95) rotate(90deg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .feather-icon {
    margin: 0;
  }
}

.selection-alert {
  background: linear-gradient(135deg, #fff6e6 0%, #fff1d6 100%);
  border: 2px solid #ffd8a8;
  border-radius: 12px;
  color: #a8641b;
  font-weight: 600;
  padding: 1rem 1.25rem;
  box-shadow: 0 4px 12px rgba(255, 216, 168, 0.2);
  animation: slideUpFade 0.5s ease-out both;
}

.student-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.border-primary {
    border: 2px solid #7367f0 !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.students-count-badge {
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
  }
}

.gr-badge {
  font-weight: 600;
  min-width: 60px;
  font-size: 0.85rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
}

/* Marks saved badge styling */
.marks-saved-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  display: inline-flex;
  align-items: center;
  animation: slideUpFade 0.3s ease-out;
}

.name-text.text-success {
  color: #059669 !important;
  font-weight: 700;
}

/* Loading spinner enhancements */
.text-center {
  .b-spinner {
    color: #7c5dfa;
  }

  p {
    color: #6b7280;
    font-weight: 600;
    margin-top: 1rem;
  }
}

/* Info alert styling */
.b-alert-info {
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  border: 2px solid #bae6fd;
  border-radius: 12px;
  color: #0369a1;
  font-weight: 600;
  padding: 1rem 1.25rem;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
  animation: slideUpFade 0.5s ease-out both;
}

/* Marks saved badge styling */
.marks-saved-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  display: inline-flex;
  align-items: center;
  animation: slideUpFade 0.3s ease-out;
}

.name-text.text-success {
  color: #059669 !important;
  font-weight: 700;
}

/* Comment Section Styles */
.save-comments-header {
  background: transparent;
  border: none;
  padding: 0.5rem 0;
  box-shadow: none;
  margin-bottom: 1rem !important;

  h6 {
    color: #4b5563;
    font-size: 0.95rem;
    letter-spacing: 0;
    font-weight: 500;
  }
}

.save-all-comments-btn {
  font-weight: 700;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.875rem;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.35);
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
}

.action-arrow-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(124, 93, 250, 0.4);
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6ff5 100%);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}

.comments-section {
  background: transparent;
  margin-top: 1.5rem;
  padding: 0;
}

/* Comment Row Details (Inside Table) - Modern Style */
.comment-row-details {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  padding: 1.5rem;
  animation: slideDownFade 0.3s ease-out;
  position: relative;
  border-top: none;
  margin-top: 0;
}

/* Hide table row when comment section is expanded */
::v-deep .students-table {
  tbody tr[aria-expanded="true"] {
    display: none !important;
  }
  
  /* Alternative: Hide row that has expanded details */
  tbody tr.b-table-has-row-details[aria-expanded="true"] {
    display: none !important;
  }
  
  /* Make sure row-details appears seamlessly */
  tbody tr + tr td.comment-row-details {
    border-top: none !important;
    padding-top: 0 !important;
  }
}

.comment-card-modern {
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem;
  border: 1.5px solid #f0edff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.15);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }
  }
}

.comment-header-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0edff;
}

.comment-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.comment-title-icon {
  color: #7c5dfa;
}

.comment-label-modern {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.95rem;
  color: #2d1b69;
  text-transform: none;
  letter-spacing: -0.2px;
}

.select-comment-btn-modern {
  font-weight: 600;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  box-shadow: 0 3px 10px rgba(124, 93, 250, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-size: 0.875rem;
  letter-spacing: 0.3px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(124, 93, 250, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

.comment-textarea-modern {
  border-radius: 12px;
  border: 2px solid #e0d8ff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
  resize: vertical;
  padding: 1rem;
  background: #ffffff;
  color: #1f2937;
  line-height: 1.6;
  min-height: 100px;
  width: 100%;
  font-weight: 500;

  &::placeholder {
    color: #9ca3af;
    font-style: normal;
    font-weight: 400;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.1);
    background: #ffffff;
    outline: none;
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
    border-color: #e5e7eb;
    color: #6b7280;
  }
}

.comment-hint-inline {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
  
  .feather-icon {
    color: #9ca3af;
  }
}

@keyframes slideDownFade {
  from {
    opacity: 0;
    transform: translateY(-10px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 1000px;
  }
}

/* Comment Selection Modal Styles */
::v-deep .comment-selection-modal {
  .modal-dialog {
    max-width: 800px;
    margin: 2rem auto;
  }

  .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-header {
    background: #ffffff;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    border-radius: 16px 16px 0 0;

    h5 {
      color: #1f2937;
      font-weight: 600;
      font-size: 1rem;
    }

    .btn-close {
      color: #6b7280;
      opacity: 0.8;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: 0;
    max-height: 70vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.comment-modal-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 70vh;
  overflow: hidden;
}

.comment-search-wrapper {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
  margin-bottom: 1rem;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: #7c5dfa;
    background: white;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.1);
  }

  .search-icon {
    color: #9ca3af;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }

  .comment-search-input {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 0.95rem;
    flex: 1;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      box-shadow: none;
    }
  }

  .clear-search-btn {
    padding: 0.25rem;
    color: #9ca3af;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.5rem;
    flex-shrink: 0;

    &:hover {
      color: #7c5dfa;
      background: #f3f4f6;
      border-radius: 6px;
    }
  }
}

.search-results-count {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.5rem;
  font-weight: 500;
}

.comments-list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  max-height: calc(70vh - 250px);
  min-height: 200px;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 10px;
    margin: 0.5rem 0;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #7c5dfa 0%, #9d7aff 100%);
    border-radius: 10px;
    border: 2px solid #f3f4f6;

    &:hover {
      background: linear-gradient(180deg, #6d4cdb 0%, #8b6ff5 100%);
    }
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #7c5dfa #f3f4f6;
}

.comments-list-scrollable {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.comment-item-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #f0edff;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.15);
    border-color: #d4c7ff;
    background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);

    &::before {
      transform: scaleY(1);
    }

    .comment-select-arrow {
      color: #7c5dfa;
      transform: translateX(3px);
    }
  }

  &:active {
    transform: translateX(3px);
  }
}

.comment-item-content {
  flex: 1;
  min-width: 0;
  padding-right: 1rem;
}

.comment-text-preview {
  font-size: 0.95rem;
  color: #1f2937;
  line-height: 1.7;
  margin-bottom: 0.75rem;
  word-wrap: break-word;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  .comment-icon {
    display: none;
  }

  span {
    flex: 1;
    display: -webkit-box;
    line-clamp: 4;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.comment-category {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #5b21b6;
  background: #ede9fe;
  padding: 0.375rem 0.625rem;
  border-radius: 8px;
  font-weight: 600;
  border: 1px solid #ddd6fe;

  .feather-icon {
    color: #7c5dfa;
  }
}

.comment-date {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;

  .feather-icon {
    color: #9ca3af;
  }
}

.comment-select-arrow {
  color: #9ca3af;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  
  .feather-icon {
    transition: transform 0.3s ease;
  }
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f9fafb;
}

.empty-comments-state {
  text-align: center;
  padding: 3rem 2rem;

  .empty-state-icon-wrapper {
    margin-bottom: 1.5rem;

    .empty-icon {
      color: #cbb9ff;
      opacity: 0.6;
    }
  }

  .empty-state-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2d1b69;
    margin-bottom: 0.5rem;
  }

  .empty-state-message {
    font-size: 0.9rem;
    color: #6b7280;
    margin: 0;
  }
}

.comment-pagination-wrapper {
  border-top: 2px solid #f3f4f6;
  padding-top: 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pagination-info {
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
}

.comment-pagination {
  ::v-deep .page-item {
    .page-link {
      border-radius: 8px;
      border-color: #e5e7eb;
      color: #6b7280;
      margin: 0 0.25rem;
      padding: 0.5rem 0.75rem;
      transition: all 0.2s ease;

      &:hover {
        background: #f3f4f6;
        border-color: #7c5dfa;
        color: #7c5dfa;
      }
    }

    &.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: white;
    }
  }
}

/* Preview Marks Modal Styles */
.comment-actions-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.preview-marks-btn-modern {
  font-weight: 700;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  box-shadow: 0 3px 10px rgba(124, 93, 250, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #7c5dfa;
  font-size: 0.875rem;
  letter-spacing: 0.3px;
  background: #ffffff;
  color: #7c5dfa;

  svg {
    color: #7c5dfa;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(124, 93, 250, 0.25);
    background: #f8f7ff;
    border-color: #6d4cdb;
    color: #6d4cdb;

    svg {
      color: #6d4cdb;
    }
  }

  &:active {
    transform: translateY(0);
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

::v-deep .preview-marks-modal {
  .modal-dialog {
    max-width: 1200px;
    margin: 2rem auto;
  }

  .modal-content {
    border-radius: 20px;
    border: none;
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    background: #ffffff;
    animation: slideUpFade 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .modal-header {
    background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
    color: #1f2937;
    border-bottom: 2px solid #e0d8ff;
    padding: 0;
    border-radius: 20px 20px 0 0;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  }

  .modal-body {
    padding: 0;
    max-height: 80vh;
    overflow-y: auto;
    background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  }
}

.preview-marks-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1.5rem 2rem;
  position: relative;
  z-index: 1;

  .header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .header-icon-wrapper {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.25);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    svg {
      color: #ffffff !important;
      stroke-width: 2.5;
    }
  }

  .header-text {
    h5 {
      color: #1f2937 !important;
      font-weight: 800;
      font-size: 1.4rem;
      letter-spacing: -0.3px;
      margin-bottom: 0.25rem;
    }

    small {
      color: #6b7280 !important;
      font-size: 0.9rem;
      font-weight: 500;
    }
  }

  .modal-close-btn {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #f3f4f6;
    border: none;
    color: #6b7280 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0;
    opacity: 0.8;

    &:hover {
      background: #e5e7eb;
      transform: rotate(90deg);
      opacity: 1;
      color: #1f2937 !important;
    }

    svg {
      stroke-width: 2.5;
      color: inherit !important;
    }
  }
}

.preview-marks-content {
  padding: 2rem;
  animation: slideUpFade 0.5s ease-out both;
}

.student-info-header {
  margin-bottom: 2rem;
  animation: slideUpFade 0.4s ease-out both;
}

.student-info-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 2px solid #e0d8ff;
  border-radius: 18px;
  padding: 1.75rem;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.12);
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(115, 103, 240, 0.18);
    border-color: #b8a9ff;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
}

.info-item {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border: 1.5px solid #f0edff;
  border-radius: 14px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUpFade 0.5s ease-out both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.15);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }

    .info-icon-wrapper {
      transform: scale(1.1) rotate(5deg);
      box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
    }
  }
}

.info-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  svg {
    color: white;
    stroke-width: 2.5;
  }
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}

.info-label {
  font-weight: 700;
  color: #6b7280;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.info-value {
  font-weight: 700;
  color: #1f2937;
  font-size: 1rem;
  line-height: 1.3;
  word-break: break-word;
}

.marks-table-wrapper {
  margin-bottom: 0;
  border-radius: 18px;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.12);
  background: #ffffff;
  border: 2px solid #e0d8ff;
  animation: slideUpFade 0.6s ease-out both;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 12px 32px rgba(115, 103, 240, 0.18);
    border-color: #b8a9ff;
  }
}

.preview-marks-block {
  margin-bottom: 2rem;

  &--assessment {
    .marks-table-wrapper {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-top: none;
    }
  }
}

.preview-section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.35rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 2px solid #e0d8ff;
  border-bottom: none;
  border-radius: 18px 18px 0 0;
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.08);
}

.preview-section-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.35);
}

.preview-section-header-text {
  min-width: 0;
}

.preview-section-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.2px;
}

.preview-section-subtitle {
  display: block;
  margin-top: 0.2rem;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 500;
}

.marks-preview-table {
  margin-bottom: 0;
  min-width: 760px;

  ::v-deep thead {
    background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
    position: relative;
    overflow: hidden;
    border-bottom: 2px solid #e0d8ff;

    th {
      color: #1f2937 !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 1px;
      padding: 1.25rem 1rem;
      border: none;
      position: relative;
      z-index: 1;
      background: transparent !important;

      * {
        color: #1f2937 !important;
      }
    }
  }

  ::v-deep tbody {
    tr {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-left: 4px solid transparent;
      animation: slideUpFade 0.5s ease-out both;
      position: relative;

      @for $i from 1 through 10 {
        &:nth-child(#{$i}) {
          animation-delay: #{$i * 0.05}s;
        }
      }

      &:nth-child(even) {
        background: #fafafa;
      }

      &:nth-child(odd) {
        background: #ffffff;
      }

      &:hover {
        background: linear-gradient(90deg, #f3f4f6 0%, #ffffff 50%, #f9fafb 100%) !important;
        border-left-color: #7c5dfa;
        transform: translateX(6px);
        box-shadow: -6px 0 16px rgba(124, 93, 250, 0.2), 0 4px 12px rgba(0, 0, 0, 0.05);
        z-index: 1;
        position: relative;
      }

      td {
        padding: 1.25rem 1rem;
        vertical-align: middle;
        border-top: 1px solid #f3f4f6;
        transition: all 0.3s ease;
      }
    }
  }
}

.subject-name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;

  .subject-icon {
    color: #7c5dfa;
    transition: all 0.3s ease;
  }

  .font-weight-bold {
    font-weight: 700;
    color: #1f2937;
    transition: color 0.3s ease;
  }
}

tr:hover .subject-name-cell {
  .subject-icon {
    transform: scale(1.2) rotate(5deg);
    color: #6d4cdb;
  }

  .font-weight-bold {
    color: #7c5dfa;
  }
}

.teacher-name-cell {
  color: #6b7280;
  font-weight: 600;
  transition: all 0.3s ease;
}

tr:hover .teacher-name-cell {
  color: #4b5563;
  font-weight: 700;
}

.marks-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 0.625rem 1rem;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  min-width: 100px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  .obtained-marks {
    font-weight: 900;
    font-size: 1.15rem;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
    letter-spacing: -0.3px;

    &.text-success {
      color: #047857 !important;
    }

    &.text-danger {
      color: #b91c1c !important;
    }

    &.text-muted {
      color: #64748b !important;
    }
  }

  .marks-separator {
    color: #cbd5e1;
    margin: 0 0.25rem;
    font-weight: 600;
    font-size: 1rem;
    position: relative;
    z-index: 1;
  }

  .total-marks {
    color: #64748b;
    font-weight: 700;
    font-size: 1rem;
    position: relative;
    z-index: 1;
  }
}

tr:hover .marks-cell {
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  border-color: #cbd5e1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);

  &::after {
    transform: scaleX(1);
  }

  .obtained-marks {
    transform: scale(1.08);
  }
}

.percentage-badge {
  font-size: 0.8125rem;
  font-weight: 900;
  padding: 0.5rem 0.875rem;
  min-width: 70px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid;
  letter-spacing: 0.2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &.badge-success {
    background: #ecfdf5;
    color: #047857;
    border-color: #10b981;
    
    &::before {
      background: rgba(16, 185, 129, 0.1);
    }
  }
  
  &.badge-info {
    background: #eff6ff;
    color: #1e40af;
    border-color: #3b82f6;
    
    &::before {
      background: rgba(59, 130, 246, 0.1);
    }
  }
  
  &.badge-warning {
    background: #fffbeb;
    color: #92400e;
    border-color: #f59e0b;
    
    &::before {
      background: rgba(245, 158, 11, 0.1);
    }
  }
  
  &.badge-danger {
    background: #fef2f2;
    color: #991b1b;
    border-color: #ef4444;
    
    &::before {
      background: rgba(239, 68, 68, 0.1);
    }
  }
  
  &.badge-secondary {
    background: #f8fafc;
    color: #475569;
    border-color: #94a3b8;
    
    &::before {
      background: rgba(148, 163, 184, 0.1);
    }
  }
}

tr:hover .percentage-badge {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::before {
    opacity: 1;
  }
}

.grade-badge {
  font-size: 0.875rem;
  font-weight: 900;
  padding: 0.5rem 0.875rem;
  min-width: 50px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &.badge-success {
    background: #ecfdf5;
    color: #047857;
    border-color: #10b981;
    
    &::before {
      background: rgba(16, 185, 129, 0.1);
    }
  }
  
  &.badge-info {
    background: #eff6ff;
    color: #1e40af;
    border-color: #3b82f6;
    
    &::before {
      background: rgba(59, 130, 246, 0.1);
    }
  }
  
  &.badge-primary {
    background: #f5f3ff;
    color: #5b21b6;
    border-color: #7c5dfa;
    
    &::before {
      background: rgba(124, 93, 250, 0.1);
    }
  }
  
  &.badge-warning {
    background: #fffbeb;
    color: #92400e;
    border-color: #f59e0b;
    
    &::before {
      background: rgba(245, 158, 11, 0.1);
    }
  }
  
  &.badge-danger {
    background: #fef2f2;
    color: #991b1b;
    border-color: #ef4444;
    
    &::before {
      background: rgba(239, 68, 68, 0.1);
    }
  }
  
  &.badge-secondary {
    background: #f8fafc;
    color: #475569;
    border-color: #94a3b8;
    
    &::before {
      background: rgba(148, 163, 184, 0.1);
    }
  }
}

tr:hover .grade-badge {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::before {
    opacity: 1;
  }
}

.status-badge {
  font-size: 0.8125rem;
  font-weight: 900;
  padding: 0.5rem 0.875rem;
  min-width: 80px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &.badge-success {
    background: #ecfdf5;
    color: #047857;
    border-color: #10b981;
    
    &::before {
      background: rgba(16, 185, 129, 0.1);
    }
  }
  
  &.badge-danger {
    background: #fef2f2;
    color: #991b1b;
    border-color: #ef4444;
    
    &::before {
      background: rgba(239, 68, 68, 0.1);
    }
  }
  
  &.badge-secondary {
    background: #f8fafc;
    color: #475569;
    border-color: #94a3b8;
    
    &::before {
      background: rgba(148, 163, 184, 0.1);
    }
  }
}

tr:hover .status-badge {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::before {
    opacity: 1;
  }
}

.marks-summary {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-radius: 18px;
  padding: 2rem;
  border: 2px solid #e0d8ff;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.12);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.7s ease-out both;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(115, 103, 240, 0.18);
    border-color: #b8a9ff;
  }
}

.preview-remarks-by-exam {
  margin-top: 1.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e8e4ff;
}

.exam-type-remarks-block {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 2px solid #e0d8ff;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.08);

  &:last-child {
    margin-bottom: 0;
  }
}

.exam-type-remarks-heading {
  font-size: 1.05rem;
  font-weight: 700;
  color: #5b41d9;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ece8ff;
}

.exam-type-remarks-list {
  margin: 0;
  padding: 0;
}

.exam-type-remark-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f0ff;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.remark-subject {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.remark-body {
  display: block;
  font-size: 0.95rem;
  color: #1f2937;
  line-height: 1.45;
  white-space: pre-wrap;
}

.summary-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #2d1b69;
  margin-bottom: 1.5rem;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    border-radius: 2px;
  }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
}

.summary-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  border: 2px solid #f0edff;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s ease-out both;

  @for $i from 1 through 4 {
    &:nth-child(#{$i}) {
      animation-delay: #{$i * 0.1}s;
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 12px 28px rgba(99, 102, 241, 0.2);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }

    .summary-icon {
      transform: scale(1.15) rotate(5deg);
    }
  }
}

.summary-icon {
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.summary-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1f2937;
  line-height: 1;
  transition: color 0.3s ease;
}

.summary-card:hover .summary-value {
  color: #7c5dfa;
}

.empty-marks-state {
  text-align: center;
  padding: 5rem 2rem;
  animation: slideUpFade 0.5s ease-out both;

  .empty-state-icon-wrapper {
    margin-bottom: 2rem;
    animation: slideUpFade 0.6s ease-out both;

    .empty-icon {
      color: #cbb9ff;
      opacity: 0.7;
      filter: drop-shadow(0 4px 8px rgba(203, 185, 255, 0.3));
      transition: all 0.3s ease;
    }
  }

  &:hover .empty-state-icon-wrapper .empty-icon {
    transform: scale(1.1);
    opacity: 0.9;
  }

  .empty-state-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #2d1b69;
    margin-bottom: 0.75rem;
    letter-spacing: -0.3px;
    animation: slideUpFade 0.7s ease-out both;
  }

  .empty-state-message {
    font-size: 1rem;
    color: #6b7280;
    margin: 0;
    font-weight: 500;
    animation: slideUpFade 0.8s ease-out both;
  }
}

</style>

